import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Container,
  Card,
  Text,
  Title,
  TextInput,
  Button,
  Select,
  MultiSelect,
  Badge,
  Flex,
  Box,
  Stack,
  Divider,
  Tabs,
  Collapse,
  ActionIcon,
  SimpleGrid,
  Modal,
  Group,
  Tooltip,
  Switch,
} from '@mantine/core';
import {
  IconAlertTriangle,
  IconChevronUp,
  IconChevronDown,
  IconX,
  IconCheck,
  IconDeviceFloppy,
  IconPencil,
  IconArrowsHorizontal,
  IconArrowsRightLeft,
  IconHelp,
} from '@tabler/icons-react';
import { createPortal } from 'react-dom';
import { useDocumentTitle } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import AggregatedReportsAPI from '../../api/AggregatedReportsApi';
import { HeaderMenu } from '../../components/HeaderMenu/HeaderMenu';
import {
  SelectTimePicker,
  SelectTooltip,
  useColorScheme,
  VisiLoader,
} from '../../components/GeneralComponent/GeneralComponent';
import { visiUserSession, setVisiUserSession } from '../../GeneralFunction';
import { useAuth } from '../../AuthContext';
import { pickGoalDefs, goalHasRevenueByName } from '../../components/Report/assists';
import type { AssistContext } from '../../components/Report/useAssistLoader';
import { createLatestRequestGuard } from '../../components/Report/latestRequest';
import {
  ActiveFilter,
  Benchmark,
  ALL_METRICS,
  DEFAULT_VISIBLE_METRICS,
  FunnelRow,
  GoalRow,
  LifecycleData,
  LifecycleRow,
  NetworkGoalDef,
  NetworkRow,
  NetworkApiPayload,
} from '../../components/Report/types';
import { TotalTab } from '../../components/Report/TotalTab';
import { ContributionData } from '../../components/Report/ContributionChart';
import { SourcesData } from '../../components/Report/SourcesChart';
import { CostMetric, TrendData, VelocityData } from '../../components/Report/InsightsCharts';
import { FunnelTab } from '../../components/Report/FunnelTab';
import { AdNetworkTab } from '../../components/Report/AdNetworkTab';
import {
  AggregateCustomerJourney,
  AggregateCustomerJourneyParams,
} from '../../components/Report/AggregateCustomerJourney';
import { ReportVelocityDrawer } from '../../components/CreateReport/ReportVelocityDrawer';
import {
  AdvancedFilters,
  AdvancedFiltersState,
  EMPTY_ADVANCED_FILTERS,
  advancedFiltersToApiParams,
} from '../../components/Report/AdvancedFilters';
import { SettingsDrawer } from '../../components/Report/SettingsDrawer';
import { exportAggregateReport, exportAggregateKeywords } from '../../components/Report/exportAggregate';
import {
  AggregateVariationAds,
  AggregateVariationAdsParams,
} from '../../components/Report/AggregateVariationAds';
import { AggregateResetStats } from '../../components/Report/AggregateResetStats';
import { AggregateCampaignNotes } from '../../components/Report/AggregateCampaignNotes';
import {
  AggregateConversionLog,
  AggregateConversionLogParams,
} from '../../components/Report/AggregateConversionLog';
import { SortState } from '../../components/Report/sorting';
import { AttributionHelp } from '../../components/Report/shared';

type OptionItem = { value: string; label: string; group?: string };

// Forces a non-empty string label so Mantine's searchable filter doesn't crash on null labels.
const normalizeOptions = (list: any): OptionItem[] => {
  if (!Array.isArray(list)) { return []; }
  const out: OptionItem[] = [];
  for (const raw of list) {
    if (!raw || raw.value === undefined || raw.value === null) { continue; }
    const value = String(raw.value);
    let label: string;
    if (typeof raw.label === 'string' && raw.label !== '') {
      label = raw.label;
    } else if (raw.label !== null && raw.label !== undefined && typeof raw.label !== 'object') {
      label = String(raw.label);
    } else {
      label = value || 'Unnamed';
    }
    const item: OptionItem = { value, label };
    if (raw.group !== undefined && raw.group !== null && typeof raw.group !== 'object') {
      const group = String(raw.group);
      if (group !== '') { item.group = group; }
    }
    out.push(item);
  }
  return out;
};

const normalizeGoalBreakdownRows = (list: any[]): GoalRow[] => {
  if (!Array.isArray(list)) { return []; }
  const out: GoalRow[] = [];
  // null/undefined preserves "no benchmark matched" so the cell renders neutral.
  const allowedTone = (v: any): 'good' | 'warning' | 'poor' | null =>
    v === 'good' || v === 'warning' || v === 'poor' ? v : null;
  const numOrUndef = (v: any): number | undefined => {
    const n = Number(v);
    return Number.isFinite(n) ? n : undefined;
  };
  const strOrUndef = (v: any): string | undefined =>
    typeof v === 'string' && v !== '' ? v : undefined;
  const normalizeRow = (raw: any): GoalRow => ({
    goal: typeof raw.goal === 'string' && raw.goal !== '' ? raw.goal : 'Goal',
    goal_key: strOrUndef(raw.goal_key),
    // Listed so the excluded-conversion link survives this normaliser, which drops unnamed fields.
    undefined_excluded: numOrUndef(raw.undefined_excluded),
    undefined_excluded_reason: typeof raw.undefined_excluded_reason === 'string' ? raw.undefined_excluded_reason : '',
    /* This normaliser drops anything it does not name, so the Customer Type fields have to be
       listed or the link never appears. */
    customer_type_available: numOrUndef(raw.customer_type_available),
    /* This normaliser drops anything it does not name - the split has to be listed or the
       section renders empty even though the API sent it. */
    customer_type_rows: Array.isArray(raw.customer_type_rows) ? raw.customer_type_rows : [],
    spend: typeof raw.spend === 'string' ? raw.spend : '$0.00',
    spendTone: allowedTone(raw.spendTone),
    revenue: typeof raw.revenue === 'string' ? raw.revenue : '$0.00',
    revenueTone: allowedTone(raw.revenueTone),
    conversions: Number.isFinite(raw.conversions) ? Number(raw.conversions) : 0,
    conversionsTone: allowedTone(raw.conversionsTone),
    cpa: typeof raw.cpa === 'string' && raw.cpa !== '' ? raw.cpa : '-',
    cpaTone: allowedTone(raw.cpaTone),
    roas: typeof raw.roas === 'string' && raw.roas !== '' ? raw.roas : '-',
    roasTone: allowedTone(raw.roasTone),
    visits: numOrUndef(raw.visits),
    visitsTone: allowedTone(raw.visitsTone),
    uniques: numOrUndef(raw.uniques),
    uniquesTone: allowedTone(raw.uniquesTone),
    goal_visits: numOrUndef(raw.goal_visits),
    // Average of the scores received, and how many conversions carried one.
    avg_value: strOrUndef(raw.avg_value),
    scored: numOrUndef(raw.scored),
    value_breakdown: Array.isArray(raw.value_breakdown) ? raw.value_breakdown : undefined,
    ucr: strOrUndef(raw.ucr),
    ucrTone: allowedTone(raw.ucrTone),
    cr: strOrUndef(raw.cr),
    crTone: allowedTone(raw.crTone),
    aov: strOrUndef(raw.aov),
    aovTone: allowedTone(raw.aovTone),
    epc: strOrUndef(raw.epc),
    epcTone: allowedTone(raw.epcTone),
    acv: strOrUndef(raw.acv),
    acvTone: allowedTone(raw.acvTone),
    arpu: strOrUndef(raw.arpu),
    arpuTone: allowedTone(raw.arpuTone),
    profit: strOrUndef(raw.profit),
    profitTone: allowedTone(raw.profitTone),
    roi: strOrUndef(raw.roi),
    roiTone: allowedTone(raw.roiTone),
    grade: raw.grade === 'good' || raw.grade === 'watch' || raw.grade === 'poor' ? raw.grade : '',
    grade_reason: typeof raw.grade_reason === 'string' ? raw.grade_reason : '',
    grade_rank: Number.isFinite(Number(raw.grade_rank)) ? Number(raw.grade_rank) : 4,
    device_breakdown: Array.isArray(raw.device_breakdown) ? raw.device_breakdown.map(normalizeRow) : undefined,
    hour_breakdown: Array.isArray(raw.hour_breakdown) ? raw.hour_breakdown.map(normalizeRow) : undefined,
    source_breakdown: Array.isArray(raw.source_breakdown) ? raw.source_breakdown.map(normalizeRow) : undefined,
  });
  for (const raw of list) {
    if (!raw || typeof raw !== 'object') { continue; }
    const row = normalizeRow(raw);
    if (Array.isArray(raw.variations) && raw.variations.length > 0) {
      // Per-variation rows reuse the GoalRow shape + variation identity.
      row.variations = raw.variations
        .map((v: any) => {
          if (!v || typeof v !== 'object') { return null; }
          const baseRow = normalizeRow(v);
          return {
            ...baseRow,
            variation_id: typeof v.variation_id === 'string' ? v.variation_id : '',
            variation_name: typeof v.variation_name === 'string' && v.variation_name !== ''
              ? v.variation_name
              : 'Variation',
          };
        })
        .filter((v: any) => v !== null);
    }
    out.push(row);
  }
  return out;
};

const ATTRIBUTION_TO_STRING: Record<string, string> = {
  '1': 'first_click',
  '2': 'last_click',
  '3': 'full_impact',
  '4': 'same_day',
};
const ATTRIBUTION_TO_INT: Record<string, number> = {
  first_click: 1,
  last_click: 2,
  full_impact: 3,
  same_day: 4,
};

export function Report() {
  useDocumentTitle('Visiopt - Report');
  const colorTheme = useColorScheme();
  const navigate = useNavigate();
  const { hasPermission } = useAuth();
  const { reportId: reportIdParam } = useParams<{ reportId: string }>();
  // Draft sentinel — Save View calls create_aggregated_report instead of save.
  const isNewReport = reportIdParam === 'new';
  const reportId = !isNewReport && reportIdParam ? decodeURIComponent(reportIdParam) : '';

  const [selectedMemberWebsite, setSelectedMemberWebsite] = useState('');
  const [reportName, setReportName] = useState('');
  const [reportNameEditing, setReportNameEditing] = useState(false);
  const [reportNameDraft, setReportNameDraft] = useState('');
  const [attribution, setAttribution] = useState<string | null>('first_click');
  // kaiax_lib time code: -1 all time, -2 custom range, 1 today, 5 last 7 days, ...
  const [timeRange, setTimeRange] = useState<number>(() => {
    const raw = visiUserSession()?.time;
    if (typeof raw === 'number') { return raw === 11 ? -2 : raw; }
    return -1;
  });
  const [selectedTimePicker, setSelectedTimePicker] = useState<any>(
    visiUserSession()?.time || -1
  );
  const [selectedTimePickerFromDate, setSelectedTimePickerFromDate] = useState('');
  const [selectedTimePickerToDate, setSelectedTimePickerToDate] = useState('');

  const [reportLoading, setReportLoading] = useState(false);
  const [loadedReportId, setLoadedReportId] = useState<string>('');
  const [saving, setSaving] = useState(false);
  // Counts in-flight requests so a single overlay covers all of them.
  const [inflight, setInflight] = useState(0);
  const trackRequest = <T,>(p: Promise<T>): Promise<T> => {
    setInflight((c) => c + 1);
    return p.finally(() => setInflight((c) => Math.max(0, c - 1)));
  };
  // Bumped on report load and Save View; filter UI changes do not auto-refetch.
  const [applyVersion, setApplyVersion] = useState(0);

  // Per-tab last-loaded-key gates: lazy load active tab, de-dup concurrent fetches.
  // Pagination / search / sort can fire several fetches; only the newest response may land.
  const campaignNetSeqRef         = useRef(0);
  const adsetNetSeqRef            = useRef(0);
  const goalBreakdownKeyRef       = useRef<string>('');
  const advFilterTablesKeyRef     = useRef<string>('');
  const primaryGoalKeyRef         = useRef<string>('');
  const lifecycleKeyRef           = useRef<string>('');
  const insightsChartKeyRef       = useRef<string>('');
  const funnelRowsKeyRef          = useRef<string>('');
  const campaignNetKeyRef         = useRef<string>('');
  const adsetNetKeyRef            = useRef<string>('');
  const adNetKeyRef               = useRef<string>('');
  // Funnel drilldown per-section dedup keys.
  const funnelDrillPrimaryKeyRef    = useRef<string>('');
  const funnelDrillGoalKeyRef       = useRef<string>('');
  const funnelDrillLifecycleKeyRef  = useRef<string>('');

  const [campaignSort, setCampaignSort] = useState<SortState>(null);
  const [adsetSort, setAdsetSort]       = useState<SortState>(null);
  const [adSort, setAdSort]             = useState<SortState>(null);

  const [totalGoalBreakdownOpen, setTotalGoalBreakdownOpen]     = useState(false);
  // Benchmarks need the goal list, which otherwise only loads with the Goal Breakdown section.
  const [benchmarkGoalsNeeded, setBenchmarkGoalsNeeded]         = useState(false);
  const [totalLifecycleOpen, setTotalLifecycleOpen]             = useState(false);
  const [totalInsightsOpen, setTotalInsightsOpen]               = useState(false);
  const [funnelGoalBreakdownOpen, setFunnelGoalBreakdownOpen]   = useState(false);
  const [funnelLifecycleOpen, setFunnelLifecycleOpen]           = useState(false);
  const [funnelTrafficOpen, setFunnelTrafficOpen]               = useState(false);
  const resetTabLoadedKeys = () => {
    goalBreakdownKeyRef.current        = '';
    advFilterTablesKeyRef.current      = '';
    primaryGoalKeyRef.current          = '';
    lifecycleKeyRef.current            = '';
    insightsChartKeyRef.current        = '';
    funnelRowsKeyRef.current           = '';
    campaignNetKeyRef.current          = '';
    adsetNetKeyRef.current             = '';
    adNetKeyRef.current                = '';
    funnelDrillPrimaryKeyRef.current   = '';
    funnelDrillGoalKeyRef.current      = '';
    funnelDrillLifecycleKeyRef.current = '';
  };

  type PrimaryGoalRow = {
    campaign_id: string;
    campaign_name: string;
    primary_goal_ordinal: string;
    primary_goal_name: string;
    primary_goal_type: string;
    primary_goal_key: string;
    has_test_variation?: number;
    // Every goal of the campaign, offered by the report's Primary Goal filter.
    goals?: { goal_key: string; goal_name: string }[];
  };
  const [primaryGoalRows, setPrimaryGoalRows] = useState<PrimaryGoalRow[]>([]);
  const [selectedPrimaryGoalKey, setSelectedPrimaryGoalKey] = useState<string>('');
  const [advancedFilters, setAdvancedFilters] = useState<AdvancedFiltersState>(EMPTY_ADVANCED_FILTERS);
  // Bumped by Update View so every filter dropdown reloads its options.
  const [dropdownRefreshKey, setDropdownRefreshKey] = useState(0);
  // Declared early so the ad-network fetch effect can capture them without TS2448.
  const [breakdownType, setBreakdownType] = useState<'none' | 'device' | 'hour' | 'cust_type'>('none');
  // Total Settings → Total Goal Breakdown; Funnel Settings → Funnel Goal + Traffic Breakdown. Drafts apply on Save.
  const [totalBreakdownDraft, setTotalBreakdownDraft] = useState<'none' | 'device' | 'hour' | 'source'>('none');
  const [totalBreakdown, setTotalBreakdown] = useState<'none' | 'device' | 'hour' | 'source'>('none');
  const [funnelBreakdownDraft, setFunnelBreakdownDraft] = useState<'none' | 'device' | 'hour' | 'cust_type'>('none');
  const [funnelBreakdown, setFunnelBreakdown] = useState<'none' | 'device' | 'hour' | 'cust_type'>('none');
  const [showAllAdCost, setShowAllAdCost] = useState<boolean>(false);
  // "Show Organic + Direct Traffic": adds Organic and Direct rows to the Campaign tab; off by default, like the Campaign Report.
  const [showOrganicDirect, setShowOrganicDirect] = useState<boolean>(false);
  // "Show All Ad Costs" help: the same help article (335) the campaign report opens.
  const showAllAdHelpRef = useRef<SVGSVGElement | null>(null);
  const [showAllAdHelpPos, setShowAllAdHelpPos] = useState<{ top: number; left: number } | null>(null);
  const [goalBreakdownRows, setGoalBreakdownRows] = useState<GoalRow[]>([]);
  /* Customer Type for a Goal Breakdown row. Nothing is computed with the report: the split is
     requested for ONE goal the first time that goal is opened, then cached under its key so
     reopening - or opening a second variation of the same goal - asks for nothing more.
     The request re-runs the goal's own metrics pipeline with the split switched on, which is
     why first-time + returning still equals the conversions already on screen. */
  const [aggGoalCt, setAggGoalCt] = useState<Record<string, any>>({});
  const [aggGoalCtOpen, setAggGoalCtOpen] = useState<string[]>([]);
  const [aggGoalCtLoading, setAggGoalCtLoading] = useState<string | null>(null);

  /* A variation row carries its goal's key too, so the variation id has to be part of the OPEN
     identity - otherwise opening one variation would open every variation under that goal.
     The CACHE stays keyed on the goal, because one request serves all of its variations. */
  const ctCacheKeyOf = (row: any) => String(row?.goal_key ?? '');
  const ctOpenKeyOf = (row: any) => {
    const goalKey = ctCacheKeyOf(row);
    if (goalKey === '') { return ''; }
    return row?.variation_id ? `${goalKey}|${row.variation_id}` : goalKey;
  };

  const goalCustomerType = {
    isOpen:    (row: GoalRow) => aggGoalCtOpen.includes(ctOpenKeyOf(row)),
    isLoading: (row: GoalRow) => aggGoalCtLoading === ctOpenKeyOf(row),
    errorFor:  (row: GoalRow) => !!aggGoalCt[ctCacheKeyOf(row)]?.error,
    rowsFor:   (row: any) => {
      const entry = aggGoalCt[ctCacheKeyOf(row)];
      if (!entry) { return undefined; }
      if (!row?.variation_id) { return entry.rows ?? []; }
      /* Match the variation by id, never by position - the payload only carries variations
         that have data, so positions do not line up with the rendered rows. */
      const v = (entry.variations ?? []).find((x: any) => String(x?.variation_id) === String(row.variation_id));
      return v?.customer_type_rows ?? [];
    },
    onToggle: (row: GoalRow) => {
      const openKey = ctOpenKeyOf(row);
      const cacheKey = ctCacheKeyOf(row);
      if (openKey === '') { return; }
      if (aggGoalCtOpen.includes(openKey)) {
        setAggGoalCtOpen((prev) => prev.filter((k) => k !== openKey));
        return;
      }
      if (aggGoalCt[cacheKey]) {
        setAggGoalCtOpen((prev) => [...prev, openKey]);
        return;
      }
      setAggGoalCtLoading(openKey);
      /* Same endpoint and same filters as the breakdown itself, plus the goal being opened.
         The backend computes that goal only, so this is not a second full report. */
      const ctDrilled = activeTab === 'funnel' && !!selectedFunnelId;
      AggregatedReportsAPI
        .getgoalbreakdown({
          visiopt_campaign_id: ctDrilled ? [selectedFunnelId as string] : selectedVisioptCampaigns,
          filter_visiopt_campaign_ids: selectedVisioptCampaigns,
          traffic_source: trafficSourceParam,
          ad_account_id: selectedAdAccounts,
          ad_campaign_id: selectedAdCampaigns,
          ad_set_id: selectedAdSets,
          ad_id: selectedAds,
          ...apiTimeParams,
          ...settingsParams,
          ...advancedFilterApiParams,
          attribution_model: attribution,
          test_variation_enabled: ctDrilled && testVariationActive ? 1 : 0,
          customer_type_goal_key: cacheKey,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          const list = data && data.status === 1 && Array.isArray(data.data?.goal_rows) ? data.data.goal_rows : [];
          const target = list.find((g: any) => String(g?.goal_key) === cacheKey);
          setAggGoalCt((prev) => ({
            ...prev,
            [cacheKey]: {
              rows: Array.isArray(target?.customer_type_rows) ? target.customer_type_rows : [],
              variations: Array.isArray(target?.variations) ? target.variations : [],
              error: data === null,
            },
          }));
          setAggGoalCtOpen((prev) => [...prev, openKey]);
        })
        .catch(() => {
          setAggGoalCt((prev) => ({ ...prev, [cacheKey]: { rows: [], variations: [], error: true } }));
          setAggGoalCtOpen((prev) => [...prev, openKey]);
        })
        .finally(() => setAggGoalCtLoading(null));
    },
  };


  type AdvancedFilterGoalMeta = {
    goal_ord: string;
    display_name: string;
    type: string;
    column_key: string;
  };
  type AdvancedFilterRow = Record<string, any>;
  type AdvancedFilterTablesData = {
    eligible: boolean;
    goals: AdvancedFilterGoalMeta[];
    source: AdvancedFilterRow[];
    tracking_tag: AdvancedFilterRow[];
    custom_tag: Record<string, AdvancedFilterRow[]>;
  };
  const EMPTY_ADVANCED_FILTER_TABLES: AdvancedFilterTablesData = {
    eligible: false, goals: [], source: [], tracking_tag: [], custom_tag: {},
  };
  const [advancedFilterTables, setAdvancedFilterTables] = useState<AdvancedFilterTablesData>(EMPTY_ADVANCED_FILTER_TABLES);
  // The same tables for the one funnel opened in the Funnel tab.
  const [funnelAdvancedFilterTables, setFunnelAdvancedFilterTables] = useState<AdvancedFilterTablesData>(EMPTY_ADVANCED_FILTER_TABLES);
  const funnelAdvFilterTablesKeyRef = useRef<string>('');

  type PrimaryGoalSummary = {
    spend: string;
    spendTone?: 'good' | 'warning' | 'poor' | null;
    revenue: string;
    revenueTone?: 'good' | 'warning' | 'poor' | null;
    conversions: number;
    conversionsTone?: 'good' | 'warning' | 'poor' | null;
    visits?: number;
    visitsTone?: 'good' | 'warning' | 'poor' | null;
    uniques?: number;
    uniquesTone?: 'good' | 'warning' | 'poor' | null;
    goal_visits?: number;
    avg_value?: string;
    scored?: number;
    cpa?: string;
    cpaTone?: 'good' | 'warning' | 'poor' | null;
    roas?: string;
    roasTone?: 'good' | 'warning' | 'poor' | null;
    ucr?: string;
    ucrTone?: 'good' | 'warning' | 'poor' | null;
    cr?: string;
    crTone?: 'good' | 'warning' | 'poor' | null;
    aov?: string;
    aovTone?: 'good' | 'warning' | 'poor' | null;
    epc?: string;
    epcTone?: 'good' | 'warning' | 'poor' | null;
    profit?: string;
    profitTone?: 'good' | 'warning' | 'poor' | null;
    roi?: string;
    roiTone?: 'good' | 'warning' | 'poor' | null;
  };
  const [primaryGoalSummary, setPrimaryGoalSummary] = useState<PrimaryGoalSummary | null>(null);
  const [lifecycleData, setLifecycleData] = useState<LifecycleData | null>(null);

  // Insights opens on Campaign Contribution, the first chip.
  const [insightsChart, setInsightsChart] = useState<string>('contribution');
  // Chart preferences are per-viewer conveniences, remembered in this browser.
  const readPref = <T,>(key: string, fallback: T): T => {
    try {
      const raw = window.localStorage.getItem(key);
      return raw === null ? fallback : (JSON.parse(raw) as T);
    } catch (e) {
      return fallback;
    }
  };
  const writePref = (key: string, value: unknown) => {
    try { window.localStorage.setItem(key, JSON.stringify(value)); } catch (e) { /* storage unavailable */ }
  };
  const [insightsCompare, setInsightsCompare] = useState<Record<string, boolean>>(() => readPref('visiopt_insights_compare', {}));
  const [cpcTrendMetric, setCpcTrendMetric] = useState<CostMetric>(() => (readPref<string>('visiopt_insights_cpc_metric', 'cpc') === 'cpm' ? 'cpm' : 'cpc'));
  const [velocityFrom, setVelocityFrom] = useState<string>(() => readPref('visiopt_insights_velocity_from', 'lp'));
  const handleInsightsCompareChange = (chart: string, v: boolean) => {
    setInsightsCompare((prev) => {
      const next = { ...prev, [chart]: v };
      writePref('visiopt_insights_compare', next);
      return next;
    });
  };
  const handleCpcTrendMetricChange = (v: CostMetric) => { setCpcTrendMetric(v); writePref('visiopt_insights_cpc_metric', v); };
  const handleVelocityFromChange = (v: string) => { setVelocityFrom(v); writePref('visiopt_insights_velocity_from', v); };
  type InsightsChartPayload = {
    eligible: boolean;
    chart_type: number;
    chart_data: any[];
    chart_subtitle: string;
    all_time_roas_title: string;
    contribution?: ContributionData | null;
    sources?: SourcesData | null;
    trend?: TrendData | null;
    velocity?: VelocityData | null;
  };
  const [insightsChartData, setInsightsChartData] = useState<Record<string, InsightsChartPayload>>({});
  const [aggregatedFunnelRows, setAggregatedFunnelRows] = useState<FunnelRow[]>([]);
  const [selectedFunnelId, setSelectedFunnelId] = useState<string | null>(null);
  const [funnelDrillGoalRows, setFunnelDrillGoalRows] = useState<GoalRow[]>([]);
  const [funnelDrillPrimarySummary, setFunnelDrillPrimarySummary] = useState<PrimaryGoalSummary | null>(null);
  const [funnelDrillLifecycle, setFunnelDrillLifecycle] = useState<LifecycleData | null>(null);
  const [drillPending, setDrillPending] = useState(false);
  const openFunnelDrill = useCallback((id: string | null) => {
    setSelectedFunnelId(id);
    setFunnelDrillGoalRows([]);
    setFunnelDrillPrimarySummary(null);
    setFunnelDrillLifecycle(null);
    setDrillPending(!!id);
  }, []);

  const [networkCampaignData, setNetworkCampaignData] = useState<NetworkApiPayload | null>(null);
  const [networkAdSetData, setNetworkAdSetData] = useState<NetworkApiPayload | null>(null);
  const [networkAdData, setNetworkAdData] = useState<NetworkApiPayload | null>(null);
  /* The settings the Ad rows on screen were loaded with (see useAssistLoader). */
  const [networkAdAssistContext, setNetworkAdAssistContext] = useState<AssistContext | null>(null);
  /* Only the newest Ad table request may deliver: an older one (a page, sort or configuration
     the user has already left) is dropped instead of overwriting the table - and the assist
     figures, which follow the table's rows and settings. */
  const [adFetchGuard] = useState(createLatestRequestGuard);
  // Encoded ids sent to backend as parent_*_id on drilldown.
  const [parentCampaignIds, setParentCampaignIds] = useState<string[]>([]);
  const [parentAdsetIds, setParentAdsetIds] = useState<string[]>([]);
  // Campaigns hidden in the Ad Network tabs and the Funnel traffic breakdown; saved with Update View.
  const [hiddenCampaignKeys, setHiddenCampaignKeys] = useState<string[]>([]);
  const toggleHiddenCampaign = useCallback((key: string) => {
    setHiddenCampaignKeys((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  // *SearchInput = live TextInput; *Search = debounced value sent to API.
  const NETWORK_PER_PAGE = 25;
  const [testVariationActive, setTestVariationActive] = useState(false);
  /* Test Variation refetches the whole panel, so the drawer edits a draft and only Save
     commits it. Every report query keeps reading testVariationActive. */
  const [testVariationDraft, setTestVariationDraft] = useState(false);
  const [campaignSearchInput, setCampaignSearchInput] = useState('');
  const [adsetSearchInput, setAdsetSearchInput] = useState('');
  const [adSearchInput, setAdSearchInput] = useState('');
  const [campaignSearch, setCampaignSearch] = useState('');
  const [adsetSearch, setAdsetSearch] = useState('');
  const [adSearch, setAdSearch] = useState('');
  const [campaignPage, setCampaignPage] = useState(1);
  const [adsetPage, setAdsetPage] = useState(1);
  const [adPage, setAdPage] = useState(1);
  useEffect(() => {
    const t = setTimeout(() => { setCampaignSearch(campaignSearchInput); setCampaignPage(1); }, 350);
    return () => clearTimeout(t);
  }, [campaignSearchInput]);
  useEffect(() => {
    const t = setTimeout(() => { setAdsetSearch(adsetSearchInput); setAdsetPage(1); }, 350);
    return () => clearTimeout(t);
  }, [adsetSearchInput]);
  useEffect(() => {
    const t = setTimeout(() => { setAdSearch(adSearchInput); setAdPage(1); }, 350);
    return () => clearTimeout(t);
  }, [adSearchInput]);

  const onCampaignSortChange = useCallback((next: SortState) => {
    setCampaignSort(next);
    setCampaignPage(1);
  }, []);
  const onAdsetSortChange = useCallback((next: SortState) => {
    setAdsetSort(next);
    setAdsetPage(1);
  }, []);
  const onAdSortChange = useCallback((next: SortState) => {
    setAdSort(next);
    setAdPage(1);
  }, []);
  useEffect(() => {
    setCampaignSort(null);
    setAdsetSort(null);
    setAdSort(null);
  }, [applyVersion]);

  // Picker value is a string; coerce so timeRange stays numeric for the API.
  const handleTimePickerChange = (value: any) => {
    setSelectedTimePicker(value);
    const num = typeof value === 'number' ? value : Number(value);
    if (Number.isFinite(num)) { setTimeRange(num); }
  };
  const handleTimePickerFromDateChange = (value: any) =>
    setSelectedTimePickerFromDate(value || '');
  const handleTimePickerToDateChange = (value: any) =>
    setSelectedTimePickerToDate(value || '');

  const apiTimeParams = useMemo(() => {
    // Backend reads a single report_time field: a numeric code, or "MM/DD/YYYY#MM/DD/YYYY"
    // for a custom range. Picker gives MM-DD-YYYY (or ISO YYYY-MM-DD).
    const toReportDate = (s: string): string => {
      if (!s) { return ''; }
      if (/^\d{2}-\d{2}-\d{4}$/.test(s)) { return s.replace(/-/g, '/'); }
      const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
      return m ? `${m[2]}/${m[3]}/${m[1]}` : '';
    };
    if (timeRange === -2) {
      const start = toReportDate(selectedTimePickerFromDate);
      const end   = toReportDate(selectedTimePickerToDate);
      if (start && end) {
        return { report_time: `${start}#${end}` };
      }
    }
    return { report_time: String(timeRange) };
  }, [timeRange, selectedTimePickerFromDate, selectedTimePickerToDate]);

  const [activeTab, setActiveTab] = useState<string | null>('total');
  // Page-level full-window width toggle. Persisted per user so it survives navigation and re-login.
  const [tableExpanded, setTableExpanded] = useState<boolean>(
    () => visiUserSession()?.expanded_report_view === true
  );
  const toggleTableExpanded = useCallback(() => {
    const next = !tableExpanded;
    setTableExpanded(next);
    setVisiUserSession('expanded_report_view', next);
  }, [tableExpanded]);
  const [filtersOpened, setFiltersOpened] = useState(isNewReport);

  const [selectedDomains, setSelectedDomains] = useState<string[]>([]);
  const [selectedVisioptCampaigns, setSelectedVisioptCampaigns] = useState<string[]>([]);
  const [selectedTrafficSources, setSelectedTrafficSources] = useState<string[]>([]);
  // Traffic Source filter (Sources chart items); sent with the Ad Network picks in one traffic_source list.
  const [selectedSourceItems, setSelectedSourceItems] = useState<string[]>([]);
  const [selectedAdAccounts, setSelectedAdAccounts] = useState<string[]>([]);
  const [selectedAdCampaigns, setSelectedAdCampaigns] = useState<string[]>([]);
  const [selectedAdSets, setSelectedAdSets] = useState<string[]>([]);
  const [selectedAds, setSelectedAds] = useState<string[]>([]);

  const [domainOptions, setDomainOptions] = useState<OptionItem[]>([]);
  const [visioptCampaignOptions, setVisioptCampaignOptions] = useState<OptionItem[]>([]);
  const [trafficSourceOptions, setTrafficSourceOptions] = useState<OptionItem[]>([]);
  const [sourceItemOptions, setSourceItemOptions] = useState<OptionItem[]>([]);
  const trafficSourceParam = useMemo(() => [...selectedTrafficSources, ...selectedSourceItems], [selectedTrafficSources, selectedSourceItems]);
  const [adAccountOptions, setAdAccountOptions] = useState<OptionItem[]>([]);
  const [adCampaignOptions, setAdCampaignOptions] = useState<OptionItem[]>([]);
  const [adSetOptions, setAdSetOptions] = useState<OptionItem[]>([]);
  const [adOptions, setAdOptions] = useState<OptionItem[]>([]);

  type PrimaryGoalOption = { key: string; label: string };
  const uniquePrimaryGoalOptions = useMemo<PrimaryGoalOption[]>(() => {
    const seen = new Set<string>();
    const out: PrimaryGoalOption[] = [];
    for (const row of primaryGoalRows) {
      if (!row || typeof row.primary_goal_key !== 'string') { continue; }
      const key = row.primary_goal_key.trim();
      if (key === '' || seen.has(key)) { continue; }
      const name = typeof row.primary_goal_name === 'string' ? row.primary_goal_name.trim() : '';
      seen.add(key);
      out.push({ key, label: name !== '' ? name : key });
    }
    return out;
  }, [primaryGoalRows]);

  // Every goal across the selected campaigns, so any of them can be the report's primary goal.
  const primaryGoalChoices = useMemo<PrimaryGoalOption[]>(() => {
    const seen = new Set<string>();
    const out: PrimaryGoalOption[] = [];
    for (const row of primaryGoalRows) {
      for (const g of (Array.isArray(row?.goals) ? row.goals : [])) {
        const key = typeof g?.goal_key === 'string' ? g.goal_key.trim() : '';
        if (key === '' || seen.has(key)) { continue; }
        seen.add(key);
        const name = typeof g?.goal_name === 'string' && g.goal_name.trim() !== '' ? g.goal_name.trim() : key;
        out.push({ key, label: name });
      }
    }
    return out.length > 0 ? out : uniquePrimaryGoalOptions;
  }, [primaryGoalRows, uniquePrimaryGoalOptions]);
  // Campaigns that share one configured primary goal give the report its default.
  const defaultPrimaryGoalKey = uniquePrimaryGoalOptions.length === 1 ? uniquePrimaryGoalOptions[0].key : '';

  const selectedPrimaryGoalName = useMemo(() => {
    const match = primaryGoalChoices.find((o) => o.key === selectedPrimaryGoalKey)
      ?? uniquePrimaryGoalOptions.find((o) => o.key === selectedPrimaryGoalKey);
    return match ? match.label : '';
  }, [primaryGoalChoices, uniquePrimaryGoalOptions, selectedPrimaryGoalKey]);

  const activeFilters = useMemo<ActiveFilter[]>(() => {
    const chips: ActiveFilter[] = [];
    const labelFor = (opts: { value: string; label: string }[], value: string): string | null => {
      const hit = opts.find((o) => o.value === value);
      return hit ? hit.label : null;
    };
    const pushChip = (
      key: string,
      label: string,
      opts: { value: string; label: string }[],
      value: string,
    ) => {
      const resolved = labelFor(opts, value);
      if (resolved !== null) { chips.push({ key, label, value: resolved }); }
    };

    selectedDomains.forEach((v) => pushChip(`domain:${v}`, 'Domain', domainOptions, v));
    selectedVisioptCampaigns.forEach((v) => pushChip(`visi:${v}`, 'Visiopt Campaign', visioptCampaignOptions, v));
    if (selectedPrimaryGoalName !== '') { chips.push({ key: 'pg:goal', label: 'Primary Goal', value: selectedPrimaryGoalName, fixed: true }); }
    selectedTrafficSources.forEach((v) => pushChip(`ts:${v}`, 'Ad Network', trafficSourceOptions, v));
    selectedAdAccounts.forEach((v) => pushChip(`aa:${v}`, 'Ad Account', adAccountOptions, v));
    selectedAdCampaigns.forEach((v) => pushChip(`ac:${v}`, 'Campaign', adCampaignOptions, v));
    selectedAdSets.forEach((v) => pushChip(`as:${v}`, 'Ad Set', adSetOptions, v));
    selectedAds.forEach((v) => pushChip(`ad:${v}`, 'Ad', adOptions, v));
    // A setting, not a selection, but it changes every spend figure, so it shows with the filters.
    if (showAllAdCost) { chips.push({ key: 'sa:on', label: 'Show All Ad Costs', value: 'On' }); }
    if (showOrganicDirect) { chips.push({ key: 'od:on', label: 'Show Organic + Direct Traffic', value: 'On' }); }
    return chips;
  }, [
    selectedDomains,
    selectedVisioptCampaigns,
    selectedTrafficSources,
    selectedAdAccounts,
    selectedAdCampaigns,
    selectedAdSets,
    selectedAds,
    domainOptions,
    visioptCampaignOptions,
    trafficSourceOptions,
    adAccountOptions,
    adCampaignOptions,
    adSetOptions,
    adOptions,
    showAllAdCost,
    showOrganicDirect,
    selectedPrimaryGoalName,
  ]);

  const removeFilter = (key: string) => {
    const sepIdx = key.indexOf(':');
    if (sepIdx === -1) return;
    const prefix = key.slice(0, sepIdx);
    const value = key.slice(sepIdx + 1);

    switch (prefix) {
      case 'domain':
        setSelectedDomains((prev) => prev.filter((v) => v !== value));
        break;
      case 'visi':
        setSelectedVisioptCampaigns((prev) => prev.filter((v) => v !== value));
        break;
      case 'ts':
        setSelectedTrafficSources((prev) => prev.filter((v) => v !== value));
        break;
      case 'aa':
        setSelectedAdAccounts((prev) => prev.filter((v) => v !== value));
        break;
      case 'ac':
        setSelectedAdCampaigns((prev) => prev.filter((v) => v !== value));
        break;
      case 'as':
        setSelectedAdSets((prev) => prev.filter((v) => v !== value));
        break;
      case 'ad':
        setSelectedAds((prev) => prev.filter((v) => v !== value));
        break;
      case 'sa':
        setShowAllAdCost(false);
        break;
      case 'od':
        setShowOrganicDirect(false);
        break;
      default:
        break;
    }
  };

  const clearAllFilters = () => {
    setSelectedDomains([]);
    setSelectedVisioptCampaigns([]);
    setSelectedTrafficSources([]);
    setSelectedSourceItems([]);
    setSelectedAdAccounts([]);
    setSelectedAdCampaigns([]);
    setSelectedAdSets([]);
    setSelectedAds([]);
  };


  // Custom Tags require exactly one Visiopt campaign; Tracking Tags work for any number.
  const advancedFiltersEnabled = selectedVisioptCampaigns.length === 1;
  const advancedFilterCampaignId = advancedFiltersEnabled ? selectedVisioptCampaigns[0] : '';
  useEffect(() => {
    if (!advancedFiltersEnabled && advancedFilters.custom.length) {
      setAdvancedFilters((prev) => ({ ...prev, custom: [] }));
    }
  }, [advancedFiltersEnabled, advancedFilters]);
  const advancedFilterApiParams = useMemo(() => advancedFiltersToApiParams(advancedFilters), [advancedFilters]);

  // Test Variation availability across the selected campaigns. Drives one message; the
  // button itself stays clickable in every state.
  const testVariationInfo = useMemo(() => {
    const withTest = primaryGoalRows.filter((r) => Number(r.has_test_variation) === 1).map((r) => r.campaign_name);
    const withoutTest = primaryGoalRows.filter((r) => Number(r.has_test_variation) !== 1).map((r) => r.campaign_name);
    let message = '';
    if (primaryGoalRows.length === 0) {
      message = '';
    } else if (withTest.length === 0) {
      message = primaryGoalRows.length === 1
        ? 'Test Variation is not available for this campaign because no test is configured.'
        : 'Test Variation is not available because none of the selected campaigns have a test configured.';
    } else if (withoutTest.length > 0) {
      message = `Test Variation data is shown for ${withTest.join(', ')}; ${withoutTest.join(', ')} has no test configured and stays unchanged.`;
    }
    return { message };
  }, [primaryGoalRows]);

  const hasDifferentPrimaryGoals = uniquePrimaryGoalOptions.length > 1;
  // A pick that is still one of the selected campaigns' goals stays; anything else falls back to their shared primary goal.
  useEffect(() => {
    if (uniquePrimaryGoalOptions.length === 0) {
      if (selectedPrimaryGoalKey !== '') { setSelectedPrimaryGoalKey(''); }
      return;
    }
    const known = selectedPrimaryGoalKey !== '' && primaryGoalChoices.some((o) => o.key === selectedPrimaryGoalKey);
    if (!known && selectedPrimaryGoalKey !== defaultPrimaryGoalKey) {
      setSelectedPrimaryGoalKey(defaultPrimaryGoalKey);
    }
  }, [uniquePrimaryGoalOptions, primaryGoalChoices, defaultPrimaryGoalKey, selectedPrimaryGoalKey]);

  useEffect(() => {
    const isResizeObserverLoopMessage = (msg: unknown) =>
      typeof msg === 'string' && msg.indexOf('ResizeObserver loop') !== -1;

    const onError = (e: ErrorEvent) => {
      if (isResizeObserverLoopMessage(e.message)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };
    const onRejection = (e: PromiseRejectionEvent) => {
      const reason: any = e.reason;
      const msg = typeof reason === 'string' ? reason : reason && reason.message;
      if (isResizeObserverLoopMessage(msg)) {
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    };

    // Capture phase so this runs before CRA's overlay listener.
    window.addEventListener('error', onError, true);
    window.addEventListener('unhandledrejection', onRejection, true);
    return () => {
      window.removeEventListener('error', onError, true);
      window.removeEventListener('unhandledrejection', onRejection, true);
    };
  }, []);

  // Draft mode: no row yet, just load the domain dropdown.
  useEffect(() => {
    if (!isNewReport) { return; }
    setReportLoading(true);
    AggregatedReportsAPI
      .getdomainlist()
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1 && data.data) {
          setDomainOptions(normalizeOptions(data.data.domain_list));
        }
        resetTabLoadedKeys();
        setApplyVersion((v) => v + 1);
      })
      .catch((error) => console.log(error))
      .finally(() => setReportLoading(false));
  }, [isNewReport, navigate]);

  useEffect(() => {
    if (!reportId) { return; }
    setReportLoading(true);
    AggregatedReportsAPI
      .getreportdetails({ report_id: reportId })
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (!data || data.status !== 1) {
          if (data) {
            notifications.show({
              color: 'red',
              icon: <IconX size="1.1rem" />,
              title: 'Error',
              message: data.message || 'Could not load the aggregated report.',
            });
          }
          return;
        }
        const payload = data.data || {};
        const saved = payload.saved || {};

        setDomainOptions(normalizeOptions(payload.domain_list));
        setVisioptCampaignOptions(normalizeOptions(payload.visiopt_campaign_list));
        setTrafficSourceOptions(normalizeOptions(payload.traffic_source_list));
        setAdAccountOptions(normalizeOptions(payload.ad_account_list));
        setAdCampaignOptions(normalizeOptions(payload.ad_campaign_list));
        setAdSetOptions(normalizeOptions(payload.ad_set_list));
        setAdOptions(normalizeOptions(payload.ad_list));
        const hydratedPrimaryGoals = Array.isArray(payload.primary_goals) ? payload.primary_goals : [];
        setPrimaryGoalRows(hydratedPrimaryGoals);

        setReportName(saved.report_name || '');
        setSelectedDomains(saved.system_website_id || []);
        setSelectedVisioptCampaigns(saved.visiopt_campaign_id || []);
        setSelectedTrafficSources(saved.traffic_source || []);
        setSelectedSourceItems(saved.traffic_source_item || []);
        setSelectedAdAccounts(saved.ad_account_id || []);
        setSelectedAdCampaigns(saved.ad_campaign_id || []);
        setSelectedAdSets(saved.ad_set_id || []);
        setSelectedAds(saved.ad_id || []);
        setAttribution(ATTRIBUTION_TO_STRING[String(saved.attribution_model ?? 1)] || 'first_click');
        const savedRange = typeof saved.time_range === 'number' ? saved.time_range : -1;
        setTimeRange(savedRange);
        if (savedRange === -2 && saved.custom_time_range && typeof saved.custom_time_range === 'object') {
          const startTs = Number(saved.custom_time_range.start);
          const endTs   = Number(saved.custom_time_range.end);
          if (Number.isFinite(startTs) && Number.isFinite(endTs) && startTs > 0 && endTs > 0) {
            const fmt = (ts: number) => {
              const d = new Date(ts * 1000);
              return `${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}-${d.getFullYear()}`;
            };
            const fromStr = fmt(startTs);
            const toStr   = fmt(endTs);
            setSelectedTimePickerFromDate(fromStr);
            setSelectedTimePickerToDate(toStr);
            // Picker reads start_date/end_date from session for the "11" custom-range chip.
            setVisiUserSession('time', 11);
            setVisiUserSession('start_date', fromStr);
            setVisiUserSession('end_date', toStr);
            setSelectedTimePicker(11);
          } else {
            setSelectedTimePicker(savedRange);
            setVisiUserSession('time', savedRange);
          }
        } else {
          setSelectedTimePicker(savedRange);
          setSelectedTimePickerFromDate('');
          setSelectedTimePickerToDate('');
          setVisiUserSession('time', savedRange);
        }
        const savedView = saved.view_columns && typeof saved.view_columns === 'object'
          ? saved.view_columns
          : {};
        if (Array.isArray(savedView.traffic_metrics) && savedView.traffic_metrics.length > 0) {
          // Migrate legacy labels to renamed canonical labels.
          const renameMap: Record<string, string> = {
            'Network Revenue': 'Network Reported Revenue',
            'Network Conv.':   'Network Conversion',
          };
          setTrafficMetrics(savedView.traffic_metrics.map((m: string) => renameMap[m] ?? m));
        }
        if (savedView.goal_metrics && typeof savedView.goal_metrics === 'object') {
          // Avg Rev/Unique was retired as a duplicate of Unique EPC, so older views drop it.
          const goalMetrics: Record<string, string[]> = {};
          Object.entries(savedView.goal_metrics as Record<string, string[]>).forEach(([goal, list]) => {
            goalMetrics[goal] = Array.isArray(list) ? list.filter((m) => m !== 'Avg Rev/Unique') : list;
          });
          setTrafficFunnelMetricsByGoal(goalMetrics);
        }
        setShowOrganicDirect(savedView.show_organic_direct === true);
        if (savedView.goal_enabled && typeof savedView.goal_enabled === 'object') {
          setTrafficGoalsEnabled(savedView.goal_enabled);
        }
        const savedTotalBt = savedView.total_breakdown === 'device' || savedView.total_breakdown === 'hour'
          || savedView.total_breakdown === 'source' ? savedView.total_breakdown : 'none';
        setTotalBreakdownDraft(savedTotalBt);
        setTotalBreakdown(savedTotalBt);
        const savedFunnelBt = savedView.funnel_breakdown === 'device' || savedView.funnel_breakdown === 'hour'
          || savedView.funnel_breakdown === 'cust_type' ? savedView.funnel_breakdown : 'none';
        setFunnelBreakdownDraft(savedFunnelBt);
        setFunnelBreakdown(savedFunnelBt);
        // Hydrate the saved primary goal selection from its ID-based key when available,
        // falling back to the legacy ordinal lookup for reports saved before the goal_key
        // column existed.
        if (typeof saved.selected_primary_goal_key === 'string' && saved.selected_primary_goal_key !== '') {
          setSelectedPrimaryGoalKey(saved.selected_primary_goal_key);
        } else if (typeof saved.selected_primary_goal_id === 'number' && saved.selected_primary_goal_id > 0) {
          const matched = hydratedPrimaryGoals.find(
            (g: any) => parseInt(String(g?.primary_goal_ordinal || ''), 10) === saved.selected_primary_goal_id,
          );
          const matchedKey = matched && typeof matched.primary_goal_key === 'string' ? matched.primary_goal_key.trim() : '';
          if (matchedKey !== '') {
            setSelectedPrimaryGoalKey(matchedKey);
          }
        }
        if (saved.attribute_window_type === 'lookback' || saved.attribute_window_type === 'true_journey') {
          setAttributionMode(saved.attribute_window_type);
        }
        if (typeof saved.lookback_days === 'number' && saved.lookback_days > 0) {
          const dayStr = String(saved.lookback_days);
          if (dayStr === '7' || dayStr === '30' || dayStr === '60' || dayStr === '90' || dayStr === '365') {
            setLookbackDays(dayStr);
          }
        }
        if (saved.visible_metrics && typeof saved.visible_metrics === 'object' && !Array.isArray(saved.visible_metrics)) {
          const visibleMetrics: Record<string, string[]> = {};
          Object.entries(saved.visible_metrics as Record<string, string[]>).forEach(([ctx, list]) => {
            visibleMetrics[ctx] = Array.isArray(list) ? list.filter((m) => m !== 'Avg Rev/Unique') : list;
          });
          setMetricsByContext((prev) => ({ ...prev, ...visibleMetrics }));
        }
        if (Array.isArray(saved.benchmark_settings)) {
          // Same formula as Unique EPC, so older Avg Rev/Unique benchmarks carry over to it.
          setBenchmarks((saved.benchmark_settings as Benchmark[]).map((b) =>
            b.metric === 'Avg Rev/Unique' ? { ...b, metric: 'Unique EPC' } : b
          ));
        }
        if (typeof saved.benchmarks_enabled === 'number') {
          setBenchmarksOn(saved.benchmarks_enabled === 1);
        }
        if (saved.advanced_filters && typeof saved.advanced_filters === 'object') {
          const af = saved.advanced_filters as Partial<AdvancedFiltersState>;
          setAdvancedFilters({
            tracking: Array.isArray(af.tracking) ? af.tracking : [],
            custom:   Array.isArray(af.custom)   ? af.custom   : [],
          });
        }
        if (saved.breakdown_type === 'device' || saved.breakdown_type === 'hour'
            || saved.breakdown_type === 'cust_type' || saved.breakdown_type === 'none') {
          setBreakdownType(saved.breakdown_type);
        }
        if (saved.show_all_ad_cost === 1 || saved.show_all_ad_cost === true) {
          setShowAllAdCost(true);
        } else if (saved.show_all_ad_cost === 0 || saved.show_all_ad_cost === false) {
          setShowAllAdCost(false);
        }
        if (saved.test_variation_enabled === 1 || saved.test_variation_enabled === true) {
          setTestVariationActive(true);
          setTestVariationDraft(true);
        } else if (saved.test_variation_enabled === 0 || saved.test_variation_enabled === false) {
          setTestVariationActive(false);
          setTestVariationDraft(false);
        }
        setHiddenCampaignKeys(Array.isArray(saved.hidden_campaigns) ? saved.hidden_campaigns : []);
        setCampaignPage(1); setAdsetPage(1); setAdPage(1);
        setCampaignSearchInput(''); setAdsetSearchInput(''); setAdSearchInput('');
        resetTabLoadedKeys();
        setApplyVersion((v) => v + 1);
      })
      .catch((error) => console.log(error))
      .finally(() => {
        setReportLoading(false);
        setLoadedReportId(reportId);
      });
  }, [reportId, navigate]);

  // The domain list is otherwise loaded once (draft load / report details), so Update View reloads it here.
  useEffect(() => {
    if (dropdownRefreshKey === 0) { return; }
    AggregatedReportsAPI
      .getdomainlist()
      .then((r) => (r.ok ? r.json() : null))
      .then((data) => {
        if (data && data.status === 1 && data.data) {
          setDomainOptions(normalizeOptions(data.data.domain_list));
        }
      })
      .catch((e) => console.log(e));
  }, [dropdownRefreshKey]);

  useEffect(() => {
    if (selectedDomains.length === 0) {
      setVisioptCampaignOptions([]);
      setSelectedVisioptCampaigns([]);
      return;
    }
    trackRequest(
      AggregatedReportsAPI
        .getvisioptcampaignlist({ system_website_id: selectedDomains })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1) {
            const list = normalizeOptions(data.data.visiopt_campaign_list);
            setVisioptCampaignOptions(list);
            const allowed = new Set(list.map((o) => o.value));
            setSelectedVisioptCampaigns((prev) => { const next = prev.filter((v) => allowed.has(v)); return next.length === prev.length ? prev : next; });
          }
        })
        .catch((e) => console.log(e))
    );
  }, [selectedDomains, dropdownRefreshKey]);

  useEffect(() => {
    if (selectedVisioptCampaigns.length === 0) {
      setTrafficSourceOptions([]);
      setAdAccountOptions([]);
      setSelectedTrafficSources([]);
      setSelectedAdAccounts([]);
      setSourceItemOptions([]);
      setSelectedSourceItems([]);
      return;
    }
    trackRequest(
      AggregatedReportsAPI
        .gettrafficsourcelist({ visiopt_campaign_id: selectedVisioptCampaigns })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1) {
            const tsList = normalizeOptions(data.data.traffic_source_list);
            const aaList = normalizeOptions(data.data.ad_account_list);
            setTrafficSourceOptions(tsList);
            setAdAccountOptions(aaList);
            const tsAllowed = new Set(tsList.map((o) => o.value));
            const aaAllowed = new Set(aaList.map((o) => o.value));
            setSelectedTrafficSources((prev) => { const next = prev.filter((v) => tsAllowed.has(v)); return next.length === prev.length ? prev : next; });
            setSelectedAdAccounts((prev) => { const next = prev.filter((v) => aaAllowed.has(v)); return next.length === prev.length ? prev : next; });
          }
        })
        .catch((e) => console.log(e))
    );
  }, [selectedVisioptCampaigns, dropdownRefreshKey]);

  // Traffic Source options follow the campaigns and the date range. A date change keeps every pick listed; a campaign
  // change drops picks the new campaigns do not have (All Source stays), so a hidden pick cannot empty the report.
  const sourceItemCampaignsRef = useRef<string>('');
  useEffect(() => {
    if (selectedVisioptCampaigns.length === 0) { return; }
    const campaignsKey = selectedVisioptCampaigns.join(',');
    const campaignsChanged = sourceItemCampaignsRef.current !== '' && sourceItemCampaignsRef.current !== campaignsKey;
    sourceItemCampaignsRef.current = campaignsKey;
    trackRequest(
      AggregatedReportsAPI
        .getsourceitemlist({ visiopt_campaign_id: selectedVisioptCampaigns, traffic_source: campaignsChanged ? [] : selectedSourceItems, ...apiTimeParams })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data || data.status !== 1) { return; }
          const options = normalizeOptions(data.data.source_item_list);
          setSourceItemOptions(options);
          if (campaignsChanged) {
            const available = new Set(options.map((o) => o.value));
            setSelectedSourceItems((prev) => { const next = prev.filter((v) => available.has(v)); return next.length === prev.length ? prev : next; });
          }
        })
        .catch((e) => console.log(e))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedVisioptCampaigns, apiTimeParams, dropdownRefreshKey]);

  useEffect(() => {
    if (selectedVisioptCampaigns.length === 0) {
      setPrimaryGoalRows([]);
      return;
    }
    trackRequest(
      AggregatedReportsAPI
        .getprimarygoals({ visiopt_campaign_id: selectedVisioptCampaigns })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1) {
            setPrimaryGoalRows(Array.isArray(data.data.primary_goals) ? data.data.primary_goals : []);
          }
        })
        .catch((e) => console.log(e))
    );
  }, [selectedVisioptCampaigns, dropdownRefreshKey]);

  /* Tab data only changes on Save / report load / reset stats, and all three clear the keys
     themselves, so a tab keeps what it already loaded instead of refetching on every switch. */

  /* Customer Type is fetched lazily per goal and cached, and the toggle short-circuits on a
     cache hit - so after the filters are applied it would keep serving the PREVIOUS selection's
     figures. applyVersion is the counter every other report fetch re-keys on, so dropping the
     cache with it puts Customer Type on exactly the same refresh cycle as the rest of the
     report. The open list is cleared too: a row left expanded would still show stale numbers.
     testVariationActive is included because the goal breakdown itself re-keys on it. */
  useEffect(() => {
    setAggGoalCt({});
    setAggGoalCtOpen([]);
  }, [applyVersion, testVariationActive]);

  useEffect(() => {
    if (activeTab !== 'total') { return; }
    if (!totalGoalBreakdownOpen && !benchmarkGoalsNeeded) { return; }
    // Test Variation is a Funnel tab feature; the Total tab always reports campaign attribution.
    const key = `v${applyVersion}|b${totalBreakdown}`;
    if (goalBreakdownKeyRef.current === key) { return; }
    goalBreakdownKeyRef.current = key;
    if (selectedVisioptCampaigns.length === 0) {
      setGoalBreakdownRows([]);
      return;
    }
    trackRequest(
      AggregatedReportsAPI
        .getgoalbreakdown({
          visiopt_campaign_id: selectedVisioptCampaigns,
          traffic_source: trafficSourceParam,
          ad_account_id: selectedAdAccounts,
          ad_campaign_id: selectedAdCampaigns,
          ad_set_id: selectedAdSets,
          ad_id: selectedAds,
          ...apiTimeParams,
          ...settingsParams,
          ...advancedFilterApiParams,
          attribution_model: attribution,
          test_variation_enabled: 0,
          goal_breakdown_type: totalBreakdown,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1) {
            const list = Array.isArray(data.data?.goal_rows) ? data.data.goal_rows : [];
            setGoalBreakdownRows(normalizeGoalBreakdownRows(list));
          } else {
            setGoalBreakdownRows([]);
          }
        })
        .catch((e) => {
          console.log(e);
          setGoalBreakdownRows([]);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, activeTab, testVariationActive, totalGoalBreakdownOpen, benchmarkGoalsNeeded, totalBreakdown]);

  useEffect(() => {
    if (activeTab !== 'total') { return; }
    // One campaign: every advanced table; several campaigns: the Traffic Source table only.
    const filtersActive = advancedFiltersEnabled
      ? advancedFilters.tracking.length > 0 || advancedFilters.custom.length > 0 || selectedSourceItems.length > 0
      : selectedVisioptCampaigns.length > 1 && selectedSourceItems.length > 0;
    if (!filtersActive) {
      setAdvancedFilterTables(EMPTY_ADVANCED_FILTER_TABLES);
      return;
    }
    const key = `v${applyVersion}`;
    if (advFilterTablesKeyRef.current === key) { return; }
    advFilterTablesKeyRef.current = key;
    trackRequest(
      AggregatedReportsAPI
        .getadvancedfiltertables({
          visiopt_campaign_id: selectedVisioptCampaigns,
          traffic_source: trafficSourceParam,
          ...apiTimeParams,
          ...settingsParams,
          ...advancedFilterApiParams,
          attribution_model: attribution,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1 && data.data) {
            setAdvancedFilterTables({
              eligible: !!data.data.eligible,
              goals:        Array.isArray(data.data.goals)        ? data.data.goals        : [],
              source:       Array.isArray(data.data.source)       ? data.data.source       : [],
              tracking_tag: Array.isArray(data.data.tracking_tag) ? data.data.tracking_tag : [],
              custom_tag:   (data.data.custom_tag && typeof data.data.custom_tag === 'object' && !Array.isArray(data.data.custom_tag))
                ? (data.data.custom_tag as Record<string, AdvancedFilterRow[]>)
                : {},
            });
          } else {
            setAdvancedFilterTables(EMPTY_ADVANCED_FILTER_TABLES);
          }
        })
        .catch((e) => {
          console.log(e);
          setAdvancedFilterTables(EMPTY_ADVANCED_FILTER_TABLES);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, activeTab]);

  // Funnel tab: the tables for the opened funnel only. A multi-campaign report keeps global attribution, as the funnel's goal rows do.
  useEffect(() => {
    if (activeTab !== 'funnel' || !selectedFunnelId) { return; }
    const filtersActive = advancedFiltersEnabled
      ? advancedFilters.tracking.length > 0 || advancedFilters.custom.length > 0 || selectedSourceItems.length > 0
      : selectedVisioptCampaigns.length > 1 && selectedSourceItems.length > 0;
    if (!filtersActive) {
      setFunnelAdvancedFilterTables(EMPTY_ADVANCED_FILTER_TABLES);
      return;
    }
    const key = `v${applyVersion}|${selectedFunnelId}`;
    if (funnelAdvFilterTablesKeyRef.current === key) { return; }
    funnelAdvFilterTablesKeyRef.current = key;
    setFunnelAdvancedFilterTables(EMPTY_ADVANCED_FILTER_TABLES);
    trackRequest(
      AggregatedReportsAPI
        .getadvancedfiltertables({
          visiopt_campaign_id: [selectedFunnelId],
          filter_visiopt_campaign_ids: selectedVisioptCampaigns,
          traffic_source: trafficSourceParam,
          ...apiTimeParams,
          ...settingsParams,
          ...advancedFilterApiParams,
          attribution_model: attribution,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!data || data.status !== 1 || !data.data) { setFunnelAdvancedFilterTables(EMPTY_ADVANCED_FILTER_TABLES); return; }
          setFunnelAdvancedFilterTables({
            eligible: !!data.data.eligible,
            goals:        Array.isArray(data.data.goals)        ? data.data.goals        : [],
            source:       Array.isArray(data.data.source)       ? data.data.source       : [],
            tracking_tag: Array.isArray(data.data.tracking_tag) ? data.data.tracking_tag : [],
            custom_tag:   (data.data.custom_tag && typeof data.data.custom_tag === 'object' && !Array.isArray(data.data.custom_tag))
              ? (data.data.custom_tag as Record<string, AdvancedFilterRow[]>)
              : {},
          });
        })
        .catch((e) => {
          console.log(e);
          setFunnelAdvancedFilterTables(EMPTY_ADVANCED_FILTER_TABLES);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, activeTab, selectedFunnelId]);

  useEffect(() => {
    if (activeTab !== 'total') { return; }
    if (selectedVisioptCampaigns.length === 0) {
      setPrimaryGoalSummary(null);
      return;
    }
    if (hasDifferentPrimaryGoals && !selectedPrimaryGoalKey) {
      setPrimaryGoalSummary(null);
      return;
    }
    const key = `v${applyVersion}|${selectedPrimaryGoalKey}`;
    if (primaryGoalKeyRef.current === key) { return; }
    primaryGoalKeyRef.current = key;
    trackRequest(
      AggregatedReportsAPI
        .getprimarygoalmetrics({
          visiopt_campaign_id: selectedVisioptCampaigns,
          traffic_source: trafficSourceParam,
          ad_account_id: selectedAdAccounts,
          ad_campaign_id: selectedAdCampaigns,
          ad_set_id: selectedAdSets,
          ad_id: selectedAds,
          ...apiTimeParams,
          ...settingsParams,
          ...advancedFilterApiParams,
          attribution_model: attribution,
          // The report's primary goal, by ID-based goal_key, never by name.
          selected_primary_goal_key: selectedPrimaryGoalKey,
          selected_primary_goal_name: selectedPrimaryGoalName,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1 && data.data && data.data.metrics) {
            setPrimaryGoalSummary(data.data.metrics);
          } else {
            setPrimaryGoalSummary(null);
          }
        })
        .catch((e) => {
          console.log(e);
          setPrimaryGoalSummary(null);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, activeTab]);

  useEffect(() => {
    if (activeTab !== 'total') { return; }
    if (!totalLifecycleOpen) { return; }
    if (selectedVisioptCampaigns.length === 0) {
      setLifecycleData(null);
      return;
    }
    const key = `v${applyVersion}`;
    if (lifecycleKeyRef.current === key) { return; }
    lifecycleKeyRef.current = key;
    trackRequest(
      AggregatedReportsAPI
        .getlifecycle({
          visiopt_campaign_id: selectedVisioptCampaigns,
          traffic_source: trafficSourceParam,
          ad_account_id: selectedAdAccounts,
          ad_campaign_id: selectedAdCampaigns,
          ad_set_id: selectedAdSets,
          ad_id: selectedAds,
          ...apiTimeParams,
          ...settingsParams,
          ...advancedFilterApiParams,
          attribution_model: attribution,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1 && data.data) {
            setLifecycleData(data.data as LifecycleData);
          } else {
            setLifecycleData(null);
          }
        })
        .catch((e) => {
          console.log(e);
          setLifecycleData(null);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, activeTab, totalLifecycleOpen]);

  const INSIGHTS_CHIP_TO_CHART_TYPE: Record<string, number> = { roas_trend: 1, rev_vs_spend: 2, cpc_trends: 4, velocity: 7, contribution: 8, sources: 9 };
  useEffect(() => {
    if (activeTab !== 'total') { return; }
    if (!totalInsightsOpen) { return; }
    const chartType = INSIGHTS_CHIP_TO_CHART_TYPE[insightsChart];
    if (!chartType) { return; }
    if (selectedVisioptCampaigns.length === 0) {
      setInsightsChartData((prev) => ({ ...prev, [insightsChart]: { eligible: false, chart_type: chartType, chart_data: [], chart_subtitle: '', all_time_roas_title: '' } }));
      return;
    }
    // Every chart follows the picked primary goal; Compare and Velocity's From step are per-chart extras.
    const compareFlag = insightsCompare[insightsChart] ? 1 : 0;
    const key = `v${applyVersion}|${insightsChart}|${selectedPrimaryGoalKey}|c${compareFlag}`
      + (insightsChart === 'velocity' ? `|${velocityFrom}` : '');
    if (insightsChartKeyRef.current === key) { return; }
    insightsChartKeyRef.current = key;
    trackRequest(
      AggregatedReportsAPI
        .getinsightschart({
          visiopt_campaign_id: selectedVisioptCampaigns,
          traffic_source: trafficSourceParam,
          ad_account_id: selectedAdAccounts,
          ad_campaign_id: selectedAdCampaigns,
          ad_set_id: selectedAdSets,
          ad_id: selectedAds,
          ...apiTimeParams,
          ...settingsParams,
          ...advancedFilterApiParams,
          attribution_model: attribution,
          chart_type: chartType,
          // ID-based goal_key; every chart resolves each campaign's primary goal from it.
          selected_primary_goal_key: selectedPrimaryGoalKey,
          compare: compareFlag,
          velocity_from: velocityFrom,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1 && data.data) {
            setInsightsChartData((prev) => ({
              ...prev,
              [insightsChart]: {
                eligible:           !!data.data.eligible,
                chart_type:         (typeof data.data.chart_type === 'number') ? data.data.chart_type : chartType,
                chart_data:         Array.isArray(data.data.chart_data)        ? data.data.chart_data : [],
                chart_subtitle:     typeof data.data.chart_subtitle === 'string' ? data.data.chart_subtitle : '',
                all_time_roas_title:typeof data.data.all_time_roas_title === 'string' ? data.data.all_time_roas_title : '',
                contribution:       (data.data.contribution && typeof data.data.contribution === 'object') ? data.data.contribution : null,
                sources:            (data.data.sources && typeof data.data.sources === 'object') ? data.data.sources : null,
                trend:              (data.data.trend && typeof data.data.trend === 'object') ? data.data.trend : null,
                velocity:           (data.data.velocity && typeof data.data.velocity === 'object') ? data.data.velocity : null,
              },
            }));
          }
        })
        .catch((e) => console.log(e))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, insightsChart, activeTab, totalInsightsOpen, selectedPrimaryGoalKey, insightsCompare, velocityFrom]);

  useEffect(() => {
    if (activeTab !== 'funnel') { return; }
    if (selectedVisioptCampaigns.length === 0) {
      setAggregatedFunnelRows([]);
      return;
    }
    const tvFlag = testVariationActive ? 1 : 0;
    const key = `v${applyVersion}|tv${tvFlag}`;
    if (funnelRowsKeyRef.current === key) { return; }
    funnelRowsKeyRef.current = key;
    trackRequest(
      AggregatedReportsAPI
        .getfunnelrows({
          test_variation_enabled: tvFlag,
          visiopt_campaign_id: selectedVisioptCampaigns,
          traffic_source: trafficSourceParam,
          ad_account_id: selectedAdAccounts,
          ad_campaign_id: selectedAdCampaigns,
          ad_set_id: selectedAdSets,
          ad_id: selectedAds,
          ...apiTimeParams,
          ...settingsParams,
          ...advancedFilterApiParams,
          attribution_model: attribution,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1 && data.data) {
            const list = Array.isArray(data.data.funnel_rows) ? data.data.funnel_rows : [];
            setAggregatedFunnelRows(list as FunnelRow[]);
          } else {
            setAggregatedFunnelRows([]);
          }
        })
        .catch((e) => {
          console.log(e);
          setAggregatedFunnelRows([]);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, activeTab, testVariationActive]);

  useEffect(() => {
    if (activeTab !== 'campaign') { return; }
    if (selectedVisioptCampaigns.length === 0) {
      setNetworkCampaignData(null);
      setNetworkAdSetData(null);
      adFetchGuard.invalidate();
      setNetworkAdData(null);
      return;
    }
    const key = `v${applyVersion}|p${campaignPage}|s${campaignSearch}|sf${campaignSort?.field ?? ''}|sd${campaignSort?.dir ?? ''}`;
    if (campaignNetKeyRef.current === key) { return; }
    campaignNetKeyRef.current = key;
    const seq = ++campaignNetSeqRef.current;
    const baseParams = {
      visiopt_campaign_id: selectedVisioptCampaigns,
      traffic_source: trafficSourceParam,
      ad_account_id: selectedAdAccounts,
      ad_campaign_id: selectedAdCampaigns,
      ad_set_id: selectedAdSets,
      ad_id: selectedAds,
      ...apiTimeParams,
      ...settingsParams,
      ...advancedFilterApiParams,
      attribution_model: attribution,
      breakdown_type: breakdownType,
      show_all_ad_cost: showAllAdCost ? 1 : 0,
    };
    trackRequest(
      AggregatedReportsAPI
        .getadnetworkdata({
          ...baseParams,
          level: 'campaign',
          page: campaignPage,
          per_page: NETWORK_PER_PAGE,
          search: campaignSearch,
          sort_field: campaignSort?.field ?? '',
          sort_dir:   campaignSort?.dir   ?? 'asc',
          hidden_campaigns: hiddenCampaignKeys,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (seq !== campaignNetSeqRef.current) { return; }
          if (data && data.status === 1 && data.data) {
            setNetworkCampaignData(data.data as NetworkApiPayload);
          } else {
            setNetworkCampaignData(null);
          }
        })
        .catch((e) => {
          console.log(e);
          if (seq !== campaignNetSeqRef.current) { return; }
          setNetworkCampaignData(null);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, campaignPage, campaignSearch, activeTab, campaignSort?.field, campaignSort?.dir]);

  useEffect(() => {
    if (activeTab !== 'adset') { return; }
    if (selectedVisioptCampaigns.length === 0) {
      setNetworkAdSetData(null);
      return;
    }
    const key = `v${applyVersion}|pc${parentCampaignIds.join(',')}|p${adsetPage}|s${adsetSearch}|sf${adsetSort?.field ?? ''}|sd${adsetSort?.dir ?? ''}`;
    if (adsetNetKeyRef.current === key) { return; }
    adsetNetKeyRef.current = key;
    const seq = ++adsetNetSeqRef.current;
    const baseParams = {
      visiopt_campaign_id: selectedVisioptCampaigns,
      traffic_source: trafficSourceParam,
      ad_account_id: selectedAdAccounts,
      ad_campaign_id: selectedAdCampaigns,
      ad_set_id: selectedAdSets,
      ad_id: selectedAds,
      ...apiTimeParams,
      ...settingsParams,
      ...advancedFilterApiParams,
      attribution_model: attribution,
      breakdown_type: breakdownType,
      show_all_ad_cost: showAllAdCost ? 1 : 0,
      level: 'adset',
      parent_campaign_id: parentCampaignIds,
      page: adsetPage,
      per_page: NETWORK_PER_PAGE,
      search: adsetSearch,
      sort_field: adsetSort?.field ?? '',
      sort_dir:   adsetSort?.dir   ?? 'asc',
      hidden_campaigns: hiddenCampaignKeys,
    };
    trackRequest(
      AggregatedReportsAPI
        .getadnetworkdata(baseParams)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (seq !== adsetNetSeqRef.current) { return; }
          if (data && data.status === 1 && data.data) {
            setNetworkAdSetData(data.data as NetworkApiPayload);
          } else {
            setNetworkAdSetData(null);
          }
        })
        .catch((e) => {
          console.log(e);
          if (seq !== adsetNetSeqRef.current) { return; }
          setNetworkAdSetData(null);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, parentCampaignIds, adsetPage, adsetSearch, activeTab, adsetSort?.field, adsetSort?.dir]);

  useEffect(() => {
    if (activeTab !== 'ad') { return; }
    if (selectedVisioptCampaigns.length === 0) {
      adFetchGuard.invalidate();
      setNetworkAdData(null);
      return;
    }
    const key = `v${applyVersion}|pc${parentCampaignIds.join(',')}|pa${parentAdsetIds.join(',')}|p${adPage}|s${adSearch}|sf${adSort?.field ?? ''}|sd${adSort?.dir ?? ''}`;
    if (adNetKeyRef.current === key) { return; }
    adNetKeyRef.current = key;
    const baseParams = {
      visiopt_campaign_id: selectedVisioptCampaigns,
      traffic_source: trafficSourceParam,
      ad_account_id: selectedAdAccounts,
      ad_campaign_id: selectedAdCampaigns,
      ad_set_id: selectedAdSets,
      ad_id: selectedAds,
      ...apiTimeParams,
      ...settingsParams,
      ...advancedFilterApiParams,
      attribution_model: attribution,
      breakdown_type: breakdownType,
      show_all_ad_cost: showAllAdCost ? 1 : 0,
      level: 'ad',
      parent_campaign_id: parentCampaignIds,
      parent_adset_id: parentAdsetIds,
      page: adPage,
      per_page: NETWORK_PER_PAGE,
      search: adSearch,
      sort_field: adSort?.field ?? '',
      sort_dir:   adSort?.dir   ?? 'asc',
      hidden_campaigns: hiddenCampaignKeys,
    };
    /* Taken with this request, from the same values it sends, so the Assisted Conversion /
       Influenced Revenue figures are later asked for under exactly the settings these rows were
       loaded with - not whatever the header or drawer shows by then. `applied` ties it to this
       application of the report, so re-applying always starts the assist cache afresh. */
    const assistContext: AssistContext = {
      report_id: reportId,
      visiopt_campaign_id: selectedVisioptCampaigns,
      attribution_model: attribution ?? '',
      report_time: (apiTimeParams as any)?.report_time ?? '-1',
      attribute_window_type: attributionMode,
      lookback_days: parseInt(lookbackDays, 10),
      applied: applyVersion,
    };
    const ticket = adFetchGuard.begin();
    trackRequest(
      AggregatedReportsAPI
        .getadnetworkdata(baseParams)
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (!adFetchGuard.isLatest(ticket)) { return; }      // superseded while in flight
          if (data && data.status === 1 && data.data) {
            setNetworkAdData(data.data as NetworkApiPayload);
            setNetworkAdAssistContext(assistContext);
          } else {
            setNetworkAdData(null);
          }
        })
        .catch((e) => {
          console.log(e);
          if (!adFetchGuard.isLatest(ticket)) { return; }
          setNetworkAdData(null);
        })
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, parentCampaignIds, parentAdsetIds, adPage, adSearch, activeTab, adSort?.field, adSort?.dir]);

  const buildDrillParams = () => ({
    visiopt_campaign_id: selectedFunnelId ? [selectedFunnelId] : [],
    filter_visiopt_campaign_ids: selectedVisioptCampaigns,
    traffic_source: trafficSourceParam,
    ad_account_id: selectedAdAccounts,
    ad_campaign_id: selectedAdCampaigns,
    ad_set_id: selectedAdSets,
    ad_id: selectedAds,
    ...apiTimeParams,
    ...settingsParams,
    ...advancedFilterApiParams,
    attribution_model: attribution,
    test_variation_enabled: testVariationActive ? 1 : 0,
    // Customer Type only splits the Traffic Breakdown, so the goal table stays ungrouped for it.
    goal_breakdown_type: funnelBreakdown === 'cust_type' ? 'none' : funnelBreakdown,
  });

  /* Leaving the Funnel tab closes the drilldown, so coming back lands on the funnel listing
     rather than whichever campaign happened to be open before. */
  useEffect(() => {
    if (activeTab !== 'funnel' && selectedFunnelId) {
      setSelectedFunnelId(null);
    }
  }, [activeTab, selectedFunnelId]);

  // Reset all drill state + dedup keys when the user leaves the drilldown.
  useEffect(() => {
    if (selectedFunnelId) { return; }
    setFunnelDrillGoalRows([]);
    setFunnelDrillPrimarySummary(null);
    setFunnelDrillLifecycle(null);
    funnelDrillPrimaryKeyRef.current   = '';
    funnelDrillGoalKeyRef.current      = '';
    funnelDrillLifecycleKeyRef.current = '';
    setDrillPending(false);
  }, [selectedFunnelId]);

  // Primary summary fetches on every drill so the panel can render immediately
  // even when every collapsible subsection stays closed.
  useEffect(() => {
    if (!selectedFunnelId) { return; }
    const tvFlag = testVariationActive ? 1 : 0;
    const key = `v${applyVersion}|f${selectedFunnelId}|tv${tvFlag}`;
    if (funnelDrillPrimaryKeyRef.current === key) { return; }
    funnelDrillPrimaryKeyRef.current = key;
    trackRequest(
      AggregatedReportsAPI.getprimarygoalmetrics(buildDrillParams())
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1 && data.data && data.data.metrics) {
            setFunnelDrillPrimarySummary(data.data.metrics);
          } else {
            setFunnelDrillPrimarySummary(null);
          }
        })
        .catch(() => setFunnelDrillPrimarySummary(null))
        .finally(() => setDrillPending(false))
    );
  }, [applyVersion, selectedFunnelId, testVariationActive]);

  // Goal Breakdown — fetched as soon as a funnel is selected. Funnel Flow uses these
  // rows to render the visualization, so gating on the accordion left Funnel Flow blank.
  useEffect(() => {
    if (!selectedFunnelId) { return; }
    const tvFlag = testVariationActive ? 1 : 0;
    const key = `v${applyVersion}|f${selectedFunnelId}|tv${tvFlag}|b${funnelBreakdown}`;
    if (funnelDrillGoalKeyRef.current === key) { return; }
    funnelDrillGoalKeyRef.current = key;
    trackRequest(
      AggregatedReportsAPI.getgoalbreakdown(buildDrillParams())
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          const list = data && data.status === 1 && Array.isArray(data.data?.goal_rows) ? data.data.goal_rows : [];
          setFunnelDrillGoalRows(normalizeGoalBreakdownRows(list));
        })
        .catch(() => setFunnelDrillGoalRows([]))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, selectedFunnelId, testVariationActive, funnelBreakdown]);

  // Lifecycle — fetches only when its section is expanded.
  useEffect(() => {
    if (!selectedFunnelId) { return; }
    if (!funnelLifecycleOpen) { return; }
    const tvFlag = testVariationActive ? 1 : 0;
    const key = `v${applyVersion}|f${selectedFunnelId}|tv${tvFlag}`;
    if (funnelDrillLifecycleKeyRef.current === key) { return; }
    funnelDrillLifecycleKeyRef.current = key;
    trackRequest(
      AggregatedReportsAPI.getlifecycle(buildDrillParams())
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1 && data.data) {
            setFunnelDrillLifecycle(data.data as LifecycleData);
          } else {
            setFunnelDrillLifecycle(null);
          }
        })
        .catch(() => setFunnelDrillLifecycle(null))
    );
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [applyVersion, selectedFunnelId, testVariationActive, funnelLifecycleOpen]);


  useEffect(() => {
    if (selectedAdAccounts.length === 0) {
      setAdCampaignOptions([]);
      setSelectedAdCampaigns([]);
      return;
    }
    trackRequest(
      AggregatedReportsAPI
        .getadcampaignlist({ ad_account_id: selectedAdAccounts })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1) {
            const list = normalizeOptions(data.data.ad_campaign_list);
            setAdCampaignOptions(list);
            const allowed = new Set(list.map((o) => o.value));
            setSelectedAdCampaigns((prev) => { const next = prev.filter((v) => allowed.has(v)); return next.length === prev.length ? prev : next; });
          }
        })
        .catch((e) => console.log(e))
    );
  }, [selectedAdAccounts, dropdownRefreshKey]);

  useEffect(() => {
    if (selectedAdAccounts.length === 0 || selectedAdCampaigns.length === 0) {
      setAdSetOptions([]);
      setSelectedAdSets([]);
      return;
    }
    trackRequest(
      AggregatedReportsAPI
        .getadsetlist({
          ad_account_id: selectedAdAccounts,
          ad_campaign_id: selectedAdCampaigns,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1) {
            const list = normalizeOptions(data.data.ad_set_list);
            setAdSetOptions(list);
            const allowed = new Set(list.map((o) => o.value));
            setSelectedAdSets((prev) => { const next = prev.filter((v) => allowed.has(v)); return next.length === prev.length ? prev : next; });
          }
        })
        .catch((e) => console.log(e))
    );
  }, [selectedAdAccounts, selectedAdCampaigns, dropdownRefreshKey]);

  useEffect(() => {
    if (selectedAdSets.length === 0) {
      setAdOptions([]);
      setSelectedAds([]);
      return;
    }
    trackRequest(
      AggregatedReportsAPI
        .getadlist({
          ad_account_id: selectedAdAccounts,
          ad_campaign_id: selectedAdCampaigns,
          ad_set_id: selectedAdSets,
        })
        .then((r) => (r.ok ? r.json() : null))
        .then((data) => {
          if (data && data.status === 1) {
            const list = normalizeOptions(data.data.ad_list);
            setAdOptions(list);
            const allowed = new Set(list.map((o) => o.value));
            setSelectedAds((prev) => { const next = prev.filter((v) => allowed.has(v)); return next.length === prev.length ? prev : next; });
          }
        })
        .catch((e) => console.log(e))
    );
  }, [selectedAdAccounts, selectedAdCampaigns, selectedAdSets, dropdownRefreshKey]);

  const [nameModalOpen, setNameModalOpen] = useState(false);
  const [nameModalDraft, setNameModalDraft] = useState('');
  const [nameModalError, setNameModalError] = useState('');

  // tvOverride carries the drawer's staged Test Variation value: setState has not flushed when
  // Save runs, so the payload would otherwise persist the previous value.
  const handleSaveView = (tvOverride?: boolean) => {
    if (!reportId && !isNewReport) { return; }
    // Empty name → prompt; covers fresh drafts and legacy rows.
    if (!reportName.trim()) {
      setNameModalDraft('');
      setNameModalError('');
      setNameModalOpen(true);
      return;
    }
    performSaveView(reportName, tvOverride);
  };

  const buildSavePayload = (nameToSave: string, tvOverride?: boolean) => ({
    report_name: nameToSave || undefined,
    system_website_id: selectedDomains,
    visiopt_campaign_id: selectedVisioptCampaigns,
    traffic_source: trafficSourceParam,
    ad_account_id: selectedAdAccounts,
    ad_campaign_id: selectedAdCampaigns,
    ad_set_id: selectedAdSets,
    ad_id: selectedAds,
    attribution_model: ATTRIBUTION_TO_INT[attribution || 'first_click'] || 1,
    ...apiTimeParams,
    view_columns: {
      traffic_metrics: trafficMetrics,
      goal_metrics: trafficFunnelMetricsByGoal,
      goal_enabled: trafficGoalsEnabled,
      total_breakdown: totalBreakdownDraft,
      funnel_breakdown: funnelBreakdownDraft,
      show_organic_direct: showOrganicDirect,
    },
    // ID-based goal key; selected_primary_goal_id (ordinal) kept only for legacy save format.
    selected_primary_goal_key: selectedPrimaryGoalKey,
    selected_primary_goal_id: 0,
    attribute_window_type: attributionMode,
    lookback_days: parseInt(lookbackDays, 10),
    visible_metrics: metricsByContext,
    benchmark_settings: benchmarks,
    benchmarks_enabled: benchmarksOn ? 1 : 0,
    advanced_filters: advancedFilters,
    breakdown_type: breakdownType,
    show_all_ad_cost: showAllAdCost ? 1 : 0,
    test_variation_enabled: (tvOverride ?? testVariationActive) ? 1 : 0,
    hidden_campaigns: hiddenCampaignKeys,
  });

  const performSaveView = (nameToSave: string, tvOverride?: boolean) => {
    if (!reportId && !isNewReport) { return; }
    setSaving(true);
    const payload = buildSavePayload(nameToSave, tvOverride);
    const request = isNewReport
      ? AggregatedReportsAPI.createreport(payload)
      : AggregatedReportsAPI.savereportview({ ...payload, report_id: reportId });

    request
      .then((response) => {
        if (response.ok) { return response.json(); }
        if (response.status === 401 || response.status === 400) {
          navigate('/logout');
        }
        return null;
      })
      .then((data) => {
        if (data && data.status === 1) {
          notifications.show({
            color: 'green',
            icon: <IconCheck size="1.1rem" />,
            title: 'Saved',
            message: isNewReport
              ? 'Aggregated report created.'
              : 'Aggregated report saved.',
          });
          setDropdownRefreshKey((k) => k + 1);
          setCampaignPage(1); setAdsetPage(1); setAdPage(1);
          setCampaignSearchInput(''); setAdsetSearchInput(''); setAdSearchInput('');
          setCampaignSearch(''); setAdsetSearch(''); setAdSearch('');
          resetTabLoadedKeys();
          if (advancedFiltersEnabled) {
            AggregatedReportsAPI
              .saveadvancedfilters({ visiopt_campaign_id: selectedVisioptCampaigns, ...advancedFilterApiParams })
              .catch((e) => console.log(e))
              .finally(() => setApplyVersion((v) => v + 1));
          } else {
            setApplyVersion((v) => v + 1);
          }
          // Swap /new URL to the real id so next save UPDATEs the same row.
          if (isNewReport && data.data && data.data.report_id) {
            navigate(`/aggregated-report/${encodeURIComponent(data.data.report_id)}`, { replace: true });
          }
        } else if (data) {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: data.message || 'Could not save the view.',
          });
        }
      })
      .catch((error) => {
        console.log(error);
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: 'Could not save the view.',
        });
      })
      .finally(() => setSaving(false));
  };

  const [settingsOpen, setSettingsOpen] = useState(false);
  const [settingsExportable, setSettingsExportable] = useState(true);
  const [settingsContext, setSettingsContext] = useState('Total Settings');
  const [settingsScope, setSettingsScope] = useState(
    'Applies to Total · Goal Breakdown'
  );
  const drawerSnapshotRef = useRef<string>('');
  const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
  const openSettings = (context: string, scope: string) => {
    setSettingsContext(context);
    setSettingsScope(scope);
    // Export builds the report-wide Ad Network file, which is not what this table shows.
    setSettingsExportable(!scope.includes('Traffic Breakdown'));
    setTestVariationDraft(testVariationActive);
    setSettingsOpen(true);
    if (context === 'Total Settings') { setBenchmarkGoalsNeeded(true); }
  };

  // Each drawer context owns its own metric slice so chip edits don't bleed across tabs.
  const [metricsByContext, setMetricsByContext] = useState<Record<string, string[]>>({
    'Total Settings': DEFAULT_VISIBLE_METRICS,
    'Funnel Settings': DEFAULT_VISIBLE_METRICS,
    'Campaign Settings': [],
    'Ad Set Settings': [],
    'Ad Settings': [],
  });
  // Tables render in the canonical picker order, not the order the user happened to toggle
  // chips in, so the Visible Metrics list and the table columns always line up.
  const orderMetrics = (list: string[], canonical: string[]) =>
    canonical.filter((m) => list.includes(m));
  const currentMetrics = metricsByContext[settingsContext] ?? [];
  const setCurrentMetrics = (m: string[]) =>
    setMetricsByContext((prev) => ({ ...prev, [settingsContext]: m }));
  const totalMetrics = orderMetrics(
    metricsByContext['Total Settings'] ?? DEFAULT_VISIBLE_METRICS,
    ALL_METRICS
  );
  const funnelAdditionalMetrics = orderMetrics(metricsByContext['Funnel Settings'] ?? [], ALL_METRICS);
  const [benchmarksOn, setBenchmarksOn] = useState(true);
  const [benchmarks, setBenchmarks] = useState<Benchmark[]>([]);
  const addBenchmark = () =>
    setBenchmarks((prev) => [
      ...prev,
      { id: `b${Date.now()}`, metric: '', goal: '', good: 0, poor: 0 },
    ]);
  const removeBenchmark = (id: string) =>
    setBenchmarks((prev) => prev.filter((b) => b.id !== id));
  const updateBenchmark = (id: string, field: keyof Benchmark, value: any) =>
    setBenchmarks((prev) => prev.map((b) => (b.id === id ? { ...b, [field]: value } : b)));

  const [lifecycleGroupBy, setLifecycleGroupBy] = useState('daily');

  const [attributionMode, setAttributionMode] = useState<'true_journey' | 'lookback'>(
    'true_journey'
  );
  const [lookbackDays, setLookbackDays] = useState<'7' | '30' | '60' | '90' | '365'>('30');

  // Top-level customer journey drawer state — wired from any tab that opens it.
  const [journeyOpen, setJourneyOpen] = useState(false);
  const [journeyParams, setJourneyParams] =
    useState<AggregateCustomerJourneyParams | null>(null);
  const [journeyEntityLabel, setJourneyEntityLabel] = useState('');

  // Variation Details: which ads produced one variation's conversions, for the drilled funnel.
  const [variationAdsOpen, setVariationAdsOpen] = useState(false);
  const [variationAdsParams, setVariationAdsParams] =
    useState<AggregateVariationAdsParams | null>(null);
  const openVariationAds = useCallback(
    (goal: any, variation: any) => {
      if (!selectedFunnelId || !goal?.goal_key || !variation?.variation_id) { return; }
      setVariationAdsParams({
        visiopt_campaign_id: [selectedFunnelId],
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        goal_key: goal.goal_key,
        goal_label: goal.goal,
        variation_id: variation.variation_id,
        variation_name: variation.variation_name,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
      });
      setVariationAdsOpen(true);
    },
    [selectedFunnelId, selectedVisioptCampaigns, attribution, apiTimeParams]
  );

  /* Campaign notes belong to the campaign, not to this report, so the drawer only needs the
     row that was clicked. The saved flag is folded back into the row so the icon updates. */
  const [campaignNotesOpen, setCampaignNotesOpen] = useState(false);
  const [campaignNotesTarget, setCampaignNotesTarget] =
    useState<{ id: string; name: string }>({ id: '', name: '' });
  const openCampaignNotes = useCallback((campaignId: string, campaignName: string) => {
    if (!campaignId) { return; }
    setCampaignNotesTarget({ id: campaignId, name: campaignName });
    setCampaignNotesOpen(true);
  }, []);
  const handleCampaignNoteSaved = useCallback((campaignId: string, hasNote: boolean) => {
    setAggregatedFunnelRows((prev) =>
      prev.map((r) => (r.id === campaignId ? { ...r, has_note: hasNote ? 1 : 0 } : r))
    );
  }, []);

  const [conversionLogOpen, setConversionLogOpen] = useState(false);
  const [conversionLogParams, setConversionLogParams] =
    useState<AggregateConversionLogParams | null>(null);

  // Conversion Log opener for the Total tab — spans every campaign in the report, read only.
  const openTotalConversionLog = useCallback(
    (goalKey: string, goalLabel: string, options?: { initialKind: 'undefined' }) => {
      if (!goalKey) { return; }
      setConversionLogParams({
        report_id: reportId,
        visiopt_campaign_id: selectedVisioptCampaigns,
        goal_key: goalKey,
        goal_label: goalLabel,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        level: 'total',
        scope_label: `Total · ${selectedVisioptCampaigns.length} campaign${selectedVisioptCampaigns.length === 1 ? '' : 's'}`,
        // Same Traffic Sources as the number clicked, so the log lists exactly those conversions.
        traffic_source: trafficSourceParam,
        initial_kind: options?.initialKind,
      });
      setConversionLogOpen(true);
    },
    [reportId, selectedVisioptCampaigns, attribution, apiTimeParams, trafficSourceParam]
  );

  // Conversion Log opener for the Funnel tab — scoped to the drilled Visiopt campaign, so
  // Move to Subscription is available and the user can see exactly what changed.
  const openFunnelConversionLog = useCallback(
    (goalKey: string, goalLabel: string, options?: { initialKind: 'undefined' }) => {
      if (!goalKey || !selectedFunnelId) { return; }
      const campaign = visioptCampaignOptions.find((o) => o.value === selectedFunnelId);
      setConversionLogParams({
        report_id: reportId,
        visiopt_campaign_id: [selectedFunnelId],
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        goal_key: goalKey,
        goal_label: goalLabel,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        level: 'funnel',
        scope_label: campaign ? campaign.label : 'Visiopt Campaign',
        traffic_source: trafficSourceParam,
        test_variation_enabled: testVariationActive ? 1 : 0,
        initial_kind: options?.initialKind,
      });
      setConversionLogOpen(true);
    },
    [reportId, selectedFunnelId, visioptCampaignOptions, selectedVisioptCampaigns, attribution, apiTimeParams, trafficSourceParam, testVariationActive]
  );

  // Goal-keyed journey opener used by Total / Funnel View Goal Breakdown tables.
  const openGoalJourney = useCallback(
    (goalKey: string, goalLabel: string) => {
      if (!goalKey) { return; }
      /* Drilled into a funnel, this is the Funnel view's own Goal Breakdown, so the journeys are
         scoped and narrowed exactly like the number that opened them. */
      setJourneyParams({
        report_id: reportId,
        visiopt_campaign_id: selectedFunnelId ? [selectedFunnelId] : selectedVisioptCampaigns,
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        goal_key: goalKey,
        journey_type: 'goal',
        test_variation_enabled: selectedFunnelId && testVariationActive ? 1 : 0,
        traffic_source: trafficSourceParam,
      });
      setJourneyEntityLabel(goalLabel);
      setJourneyOpen(true);
    },
    [reportId, selectedVisioptCampaigns, selectedFunnelId, attribution, apiTimeParams, attributionMode, lookbackDays, trafficSourceParam, testVariationActive]
  );

  /* Conversion Log for one test variation: the Funnel view's own log, narrowed to the clicked
     variation so it lists exactly the conversions that row counted. */
  const openVariationConversionLog = useCallback(
    (goal: any, variation: any) => {
      if (!goal?.goal_key || !variation?.variation_id || !selectedFunnelId) { return; }
      const campaign = visioptCampaignOptions.find((o) => o.value === selectedFunnelId);
      setConversionLogParams({
        report_id: reportId,
        visiopt_campaign_id: [selectedFunnelId],
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        goal_key: goal.goal_key,
        goal_label: `${goal.goal} - ${variation.variation_name}`,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        level: 'funnel',
        scope_label: `${campaign ? campaign.label : 'Visiopt Campaign'} · ${variation.variation_name}`,
        traffic_source: trafficSourceParam,
        test_variation_enabled: 1,
        variation_id: variation.variation_id,
      });
      setConversionLogOpen(true);
    },
    [reportId, selectedFunnelId, visioptCampaignOptions, selectedVisioptCampaigns, attribution, apiTimeParams, trafficSourceParam]
  );

  // Journey for one test variation: same goal journey, narrowed to the clicked variation so the
  // drawer lists exactly the conversions that row counted.
  const openVariationJourney = useCallback(
    (goal: any, variation: any) => {
      if (!goal?.goal_key || !variation?.variation_id) { return; }
      setJourneyParams({
        report_id: reportId,
        visiopt_campaign_id: selectedFunnelId ? [selectedFunnelId] : selectedVisioptCampaigns,
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        goal_key: goal.goal_key,
        journey_type: 'goal',
        variation_id: variation.variation_id,
        traffic_source: trafficSourceParam,
      });
      setJourneyEntityLabel(`${goal.goal} - ${variation.variation_name}`);
      setJourneyOpen(true);
    },
    [reportId, selectedFunnelId, selectedVisioptCampaigns, attribution, apiTimeParams, trafficSourceParam]
  );

  // Source-keyed journey opener used by Total / Funnel View Source advanced-filter table.
  // sourceId is the encrypted source_id the backend returns on each row; the journey API
  // decrypts it and filters by vp.source_id so results match the same rows the table aggregated.
  const openSourceJourney = useCallback(
    (args: { sourceId: string; sourceName: string; goalKey: string; goalLabel: string }) => {
      if (!args.sourceId || !args.goalKey) { return; }
      setJourneyParams({
        report_id: reportId,
        // From the Funnel tab the table belongs to the opened funnel, so its journeys do too.
        visiopt_campaign_id: activeTab === 'funnel' && selectedFunnelId ? [selectedFunnelId] : selectedVisioptCampaigns,
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        goal_key: args.goalKey,
        journey_type: 'source',
        source_id: args.sourceId,
        traffic_source: trafficSourceParam,
      });
      const label = args.sourceName
        ? `${args.sourceName} · ${args.goalLabel}`
        : args.goalLabel;
      setJourneyEntityLabel(label);
      setJourneyOpen(true);
    },
    [reportId, selectedVisioptCampaigns, attribution, apiTimeParams, attributionMode, lookbackDays, trafficSourceParam, activeTab, selectedFunnelId]
  );

  // Tracking-tag journey opener — used by the Tracking Tag advanced-filter table.
  // trackingTagData is the base64 JSON blob the AdvancedFilterTables component built from the row's
  // populated UTM cells; backend decodes it and filters utm_*_value columns on visitor_page_view.
  const openTrackingJourney = useCallback(
    (args: { trackingTagData: string; entityLabel: string; goalKey: string; goalLabel: string }) => {
      if (!args.trackingTagData || !args.goalKey) { return; }
      setJourneyParams({
        report_id: reportId,
        // From the Funnel tab the table belongs to the opened funnel, so its journeys do too.
        visiopt_campaign_id: activeTab === 'funnel' && selectedFunnelId ? [selectedFunnelId] : selectedVisioptCampaigns,
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        goal_key: args.goalKey,
        journey_type: 'tracking_tag',
        tracking_tag_data: args.trackingTagData,
        traffic_source: trafficSourceParam,
      });
      setJourneyEntityLabel(args.entityLabel ? `${args.entityLabel} · ${args.goalLabel}` : args.goalLabel);
      setJourneyOpen(true);
    },
    [reportId, selectedVisioptCampaigns, attribution, apiTimeParams, attributionMode, lookbackDays, trafficSourceParam, activeTab, selectedFunnelId]
  );

  // Custom-tag journey opener — used by Custom Tag advanced-filter sub-tables. tagId is the
  // encrypted traffic_source.id; backend decrypts it and OR-matches across traffic_source_1..5.
  const openCustomJourney = useCallback(
    (args: { tagId: string; tagValue: string; tagName: string; goalKey: string; goalLabel: string }) => {
      if (!args.tagId || !args.tagValue || !args.goalKey) { return; }
      setJourneyParams({
        report_id: reportId,
        // From the Funnel tab the table belongs to the opened funnel, so its journeys do too.
        visiopt_campaign_id: activeTab === 'funnel' && selectedFunnelId ? [selectedFunnelId] : selectedVisioptCampaigns,
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        goal_key: args.goalKey,
        journey_type: 'custom_tag',
        custom_tag_id: args.tagId,
        custom_tag_value: args.tagValue,
        traffic_source: trafficSourceParam,
      });
      const label = args.tagName
        ? `${args.tagName}: ${args.tagValue} · ${args.goalLabel}`
        : `${args.tagValue} · ${args.goalLabel}`;
      setJourneyEntityLabel(label);
      setJourneyOpen(true);
    },
    [reportId, selectedVisioptCampaigns, attribution, apiTimeParams, attributionMode, lookbackDays, trafficSourceParam, activeTab, selectedFunnelId]
  );

  const [lifecycleVelocityOpen, setLifecycleVelocityOpen] = useState(false);
  const [lifecycleVelocityParams, setLifecycleVelocityParams] = useState<Record<string, any>>({});
  const [lifecycleVelocityLabel, setLifecycleVelocityLabel] = useState('');

  const openLifecycleVelocity = useCallback(
    (args: {
      goalKey: string;
      goalLabel: string;
      cohortKey: string;
      cohortKind: 'daily' | 'range' | 'weekday' | 'weekend' | 'summary';
      cohortStart?: string;
      cohortEnd?: string;
    }) => {
      const visioptIds = selectedFunnelId ? [selectedFunnelId] : selectedVisioptCampaigns;
      const payload: Record<string, any> = {
        report_id: reportId,
        visiopt_campaign_id: visioptIds,
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        goal_key: args.goalKey,
        velocity_mode: 'goal',
        // Drawer must narrow the same way the Lifecycle panel it opened from does - which only
        // narrows on the Funnel tab, so the Total tab's Lifecycle drawer stays unnarrowed.
        test_variation_enabled: selectedFunnelId && testVariationActive ? 1 : 0,
        cohort_key: args.cohortKey,
        cohort_kind: args.cohortKind,
      };
      if (args.cohortStart) { payload.cohort_start = args.cohortStart; }
      if (args.cohortEnd)   { payload.cohort_end   = args.cohortEnd; }
      setLifecycleVelocityParams(payload);
      setLifecycleVelocityLabel(args.goalLabel);
      setLifecycleVelocityOpen(true);
    },
    [reportId, selectedVisioptCampaigns, selectedFunnelId, attribution, apiTimeParams, attributionMode, lookbackDays, testVariationActive]
  );

  // Ad-network journey opener for Funnel View → Traffic Breakdown rows. The funnel id
  // (selectedFunnelId) scopes the journey to that single Visiopt campaign.
  const openTrafficJourney = useCallback(
    (args: {
      row: NetworkRow;
      goalKey: string;
      goalLabel: string;
      level: 'campaign' | 'adset' | 'ad';
      parent_campaign_id: string;
      parent_adset_id: string;
    }) => {
      const visioptIds = selectedFunnelId ? [selectedFunnelId] : selectedVisioptCampaigns;
      setJourneyParams({
        report_id: reportId,
        visiopt_campaign_id: visioptIds,
        filter_visiopt_campaign_ids: selectedVisioptCampaigns,
        attribution_model: attribution ?? '',
        attribute_window_type: attributionMode,
        lookback_days: parseInt(lookbackDays, 10),
        report_time: (apiTimeParams as any)?.report_time ?? '-1',
        goal_key: args.goalKey,
        journey_type: 'ad_network',
        test_variation_enabled: selectedFunnelId && testVariationActive ? 1 : 0,
        traffic_source: args.row.source,
        // Organic / Direct rows hold the report's Traffic Source picks.
        report_traffic_source: trafficSourceParam,
        level: args.level,
        ad_entity_id: args.row.id,
        parent_campaign_id: args.parent_campaign_id,
        parent_adset_id: args.parent_adset_id,
      });
      setJourneyEntityLabel(
        (args.row.ad ?? args.row.adset ?? args.row.campaign ?? args.row.entity_name) || args.goalLabel
      );
      setJourneyOpen(true);
    },
    [reportId, selectedVisioptCampaigns, selectedFunnelId, attribution, apiTimeParams, attributionMode, lookbackDays, testVariationActive, trafficSourceParam]
  );

  // Spread into every report fetch so backend honours window + benchmark tones.
  const settingsParams = useMemo(() => ({
    attribute_window_type: attributionMode,
    lookback_days: parseInt(lookbackDays, 10),
    benchmark_settings: benchmarks,
    benchmarks_enabled: benchmarksOn ? 1 : 0,
    // Every table reads the report's primary goal from here.
    selected_primary_goal_key: selectedPrimaryGoalKey,
    // "Show All Ad Costs" applies to every spend figure, the strip and the charts included.
    show_all_ad_cost: showAllAdCost ? 1 : 0,
    // Campaign tab Organic / Direct rows.
    show_organic_direct: showOrganicDirect ? 1 : 0,
  }), [attributionMode, lookbackDays, benchmarks, benchmarksOn, selectedPrimaryGoalKey, showAllAdCost, showOrganicDirect]);

  const funnelTrafficBaseParams = useMemo(() => ({
    visiopt_campaign_id: selectedFunnelId ? [selectedFunnelId] : [],
    filter_visiopt_campaign_ids: selectedVisioptCampaigns,
    traffic_source: trafficSourceParam,
    ad_account_id: selectedAdAccounts,
    ad_campaign_id: selectedAdCampaigns,
    ad_set_id: selectedAdSets,
    ad_id: selectedAds,
    ...apiTimeParams,
    ...settingsParams,
    ...advancedFilterApiParams,
    attribution_model: attribution,
    test_variation_enabled: testVariationActive ? 1 : 0,
    breakdown_type: breakdownType,
    show_all_ad_cost: showAllAdCost ? 1 : 0,
  }), [
    selectedFunnelId,
    selectedVisioptCampaigns,
    trafficSourceParam,
    selectedAdAccounts,
    selectedAdCampaigns,
    selectedAdSets,
    selectedAds,
    apiTimeParams,
    settingsParams,
    advancedFilterApiParams,
    attribution,
    testVariationActive,
    breakdownType,
    showAllAdCost,
  ]);

  const [trafficMetrics, setTrafficMetrics] = useState<string[]>([
    'Uniques',
    'Visits',
    'Ad Spend',
    'Network Reported Revenue',
    'Network Conversion',
    'Impressions',
    'CTR',
    'CPC',
    'CPM',
  ]);
  const [trafficFunnelMetricsByGoal, setTrafficFunnelMetricsByGoal] = useState<
    Record<string, string[]>
  >({});
  const [trafficGoalsEnabled, setTrafficGoalsEnabled] = useState<Record<string, boolean>>({});

  const buildDrawerStateKey = () => JSON.stringify({
    m: metricsByContext,
    bm: benchmarks,
    bo: benchmarksOn,
    am: attributionMode,
    lb: lookbackDays,
    tm: trafficMetrics,
    tfm: trafficFunnelMetricsByGoal,
    tge: trafficGoalsEnabled,
    bt: breakdownType,
    tbt: totalBreakdownDraft,
    fbt: funnelBreakdownDraft,
    sa: showAllAdCost,
    tv: testVariationDraft,
  });
  const currentDrawerKey = buildDrawerStateKey();
  const drawerDirty = settingsOpen && drawerSnapshotRef.current !== '' && drawerSnapshotRef.current !== currentDrawerKey;
  // Snapshot the moment the drawer opens so dirty == any change after open.
  useEffect(() => {
    if (settingsOpen) {
      drawerSnapshotRef.current = currentDrawerKey;
    } else {
      drawerSnapshotRef.current = '';
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [settingsOpen]);

  const restoreDrawerSnapshot = () => {
    const snap = drawerSnapshotRef.current;
    if (!snap) { return; }
    try {
      const s = JSON.parse(snap);
      if (s.m)   { setMetricsByContext(s.m); }
      if (Array.isArray(s.bm)) { setBenchmarks(s.bm); }
      if (typeof s.bo === 'boolean') { setBenchmarksOn(s.bo); }
      if (s.am === 'true_journey' || s.am === 'lookback') { setAttributionMode(s.am); }
      if (s.lb === '7' || s.lb === '30' || s.lb === '60' || s.lb === '90' || s.lb === '365') { setLookbackDays(s.lb); }
      if (Array.isArray(s.tm))  { setTrafficMetrics(s.tm); }
      if (s.tfm) { setTrafficFunnelMetricsByGoal(s.tfm); }
      if (s.tge) { setTrafficGoalsEnabled(s.tge); }
      if (s.bt === 'none' || s.bt === 'device' || s.bt === 'hour') { setBreakdownType(s.bt); }
      if (s.tbt === 'none' || s.tbt === 'device' || s.tbt === 'hour' || s.tbt === 'source') { setTotalBreakdownDraft(s.tbt); }
      if (s.fbt === 'none' || s.fbt === 'device' || s.fbt === 'hour' || s.fbt === 'cust_type') { setFunnelBreakdownDraft(s.fbt); }
      if (typeof s.sa === 'boolean') { setShowAllAdCost(s.sa); }
      if (typeof s.tv === 'boolean') { setTestVariationDraft(s.tv); }
    } catch (e) {
      // Snapshot was malformed; nothing to restore.
    }
  };

  const handleDrawerSave = () => {
    setTestVariationActive(testVariationDraft);
    setTotalBreakdown(totalBreakdownDraft);
    setFunnelBreakdown(funnelBreakdownDraft);
    handleSaveView(testVariationDraft);
    setSettingsOpen(false);
  };
  const handleDrawerDiscard = () => {
    restoreDrawerSnapshot();
    setSettingsOpen(false);
  };
  const requestDrawerClose = () => {
    if (drawerDirty) {
      setCloseConfirmOpen(true);
    } else {
      setSettingsOpen(false);
    }
  };
  const confirmCloseSave = () => {
    setCloseConfirmOpen(false);
    handleDrawerSave();
  };
  const confirmCloseDiscard = () => {
    setCloseConfirmOpen(false);
    handleDrawerDiscard();
  };

  // The report's own request params; the tab export and the keyword export both send exactly these.
  const exportBaseParams = {
    report_id: reportId,
    visiopt_campaign_id: selectedVisioptCampaigns,
    traffic_source: trafficSourceParam,
    ad_account_id: selectedAdAccounts,
    ad_campaign_id: selectedAdCampaigns,
    ad_set_id: selectedAdSets,
    ad_id: selectedAds,
    ...apiTimeParams,
    ...settingsParams,
    ...advancedFilterApiParams,
    attribution_model: attribution,
    breakdown_type: breakdownType,
    goal_breakdown_type: totalBreakdown,
    show_all_ad_cost: showAllAdCost ? 1 : 0,
    test_variation_enabled: settingsContext === 'Funnel Settings' && testVariationActive ? 1 : 0,
    // Total metrics strip needs the pinned goal when campaigns disagree on their primary.
    selected_primary_goal_key: selectedPrimaryGoalKey,
    selected_primary_goal_name: selectedPrimaryGoalName,
    // Hidden campaigns are left out of the Ad Network and keyword files, as they are out of the tables.
    hidden_campaigns: hiddenCampaignKeys,
  };
  const [exporting, setExporting] = useState(false);
  const handleExport = () => {
    setExporting(true);
    exportAggregateReport({
      context: settingsContext,
      reportName,
      baseParams: exportBaseParams,
      advancedTables: advancedFilterTables,
    })
      .then((fileName) => {
        if (fileName === '') {
          notifications.show({
            color: 'red',
            icon: <IconX size="1.1rem" />,
            title: 'Error',
            message: 'No data available to export.',
          });
        }
      })
      .catch((e) => {
        console.log(e);
        notifications.show({
          color: 'red',
          icon: <IconX size="1.1rem" />,
          title: 'Error',
          message: 'Could not export the report.',
        });
      })
      .finally(() => setExporting(false));
  };

  // Every Keyword View exports the same complete file: all Google ad set and ad keywords in the report.
  const handleKeywordExport = () => exportAggregateKeywords({ reportName, baseParams: exportBaseParams });

  // Reset Stats sits outside the drawer's Cancel/Save flow; it opens its own confirmation.
  const [resetStatsOpen, setResetStatsOpen] = useState(false);
  const handleResetStatsDone = (message: string) => {
    handleDrawerDiscard();
    resetTabLoadedKeys();
    setApplyVersion((v) => v + 1);
    notifications.show({
      color: 'green',
      icon: <IconCheck size="1.1rem" />,
      title: 'Success',
      message,
    });
  };

  const [selectedCampaignIds, setSelectedCampaignIds] = useState<string[]>([]);
  const [selectedAdsetIds, setSelectedAdsetIds] = useState<string[]>([]);
  const [selectedAdIds, setSelectedAdIds] = useState<string[]>([]);

  // Campaign payload defines the canonical goal set; AdSet/Ad reuse it.
  /* The Funnel tab's Traffic Breakdown fetches its own goals, and on that tab the ad-network
     payloads below have usually never loaded - without this the drawer lists no goals at all. */
  const [funnelTrafficGoalDefs, setFunnelTrafficGoalDefs] = useState<NetworkGoalDef[]>([]);

  const networkGoalDefs: NetworkGoalDef[] = useMemo(() => {
    if (networkCampaignData && Array.isArray(networkCampaignData.goals)) {
      return networkCampaignData.goals;
    }
    if (networkAdSetData && Array.isArray(networkAdSetData.goals)) {
      return networkAdSetData.goals;
    }
    if (networkAdData && Array.isArray(networkAdData.goals)) {
      return networkAdData.goals;
    }
    return [];
  }, [networkCampaignData, networkAdSetData, networkAdData]);

  // Union of goal {key, name} across tabs; powers Settings drawer goal dropdowns.
  // Deduped by ID-based goal_key (type+linked_id) — names are display-only.
  const dynamicGoalOptions = useMemo<{ key: string; name: string }[]>(() => {
    const seenKey = new Set<string>();
    const out: { key: string; name: string }[] = [];
    const isAllowed = (key: string) => !key.startsWith('button:');
    for (const g of [...networkGoalDefs, ...funnelTrafficGoalDefs]) {
      const key = typeof g?.key === 'string' ? g.key.trim() : '';
      const name = typeof g?.name === 'string' ? g.name.trim() : '';
      if (key && isAllowed(key) && !seenKey.has(key)) { seenKey.add(key); out.push({ key, name: name || key }); }
    }
    for (const r of goalBreakdownRows as any[]) {
      const key = typeof r?.goal_key === 'string' ? r.goal_key.trim() : '';
      const name = typeof r?.goal === 'string' ? r.goal.trim() : '';
      if (key && isAllowed(key) && !seenKey.has(key)) { seenKey.add(key); out.push({ key, name: name || key }); }
    }
    return out;
  }, [networkGoalDefs, funnelTrafficGoalDefs, goalBreakdownRows]);
  const dynamicGoalNames = useMemo(() => dynamicGoalOptions.map((o) => o.name), [dynamicGoalOptions]);

  // Pre-fill defaults for new backend goals so first-load tables aren't empty. The Funnel tab's
  // Traffic Breakdown goals count too - it is often the only table loaded on that tab.
  const goalDefsNeedingDefaults = useMemo(
    () => [...networkGoalDefs, ...funnelTrafficGoalDefs],
    [networkGoalDefs, funnelTrafficGoalDefs]
  );
  /* Goal definitions per tab. has_revenue is measured at the level it was computed for, so a
     tab reads its own response rather than whichever one happened to load first - otherwise the
     Influenced Revenue column appeared or vanished depending on whether the Campaign tab had
     been opened. networkGoalDefs stays the placeholder until a tab's own payload arrives. */
  const campaignGoalDefs = useMemo(
    () => pickGoalDefs(networkCampaignData?.goals, networkGoalDefs),
    [networkCampaignData, networkGoalDefs]
  );
  const adSetGoalDefs = useMemo(
    () => pickGoalDefs(networkAdSetData?.goals, networkGoalDefs),
    [networkAdSetData, networkGoalDefs]
  );
  const adGoalDefs = useMemo(
    () => pickGoalDefs(networkAdData?.goals, networkGoalDefs),
    [networkAdData, networkGoalDefs]
  );

  /* Which goals carry money, keyed the way the drawer addresses goals. Taken from the Ad
     payload because the assist columns the flag gates exist only on the Ad tab. */
  const goalHasRevenue = useMemo(() => goalHasRevenueByName(adGoalDefs), [adGoalDefs]);

  // Pre-fill defaults for new backend goals so first-load tables aren't empty.
  useEffect(() => {
    if (goalDefsNeedingDefaults.length === 0) { return; }
    setTrafficGoalsEnabled((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const g of goalDefsNeedingDefaults) {
        if (next[g.name] === undefined) { next[g.name] = true; changed = true; }
      }
      return changed ? next : prev;
    });
    setTrafficFunnelMetricsByGoal((prev) => {
      const next = { ...prev };
      let changed = false;
      for (const g of goalDefsNeedingDefaults) {
        if (!next[g.name]) {
          next[g.name] = ['Conversions', 'CPA', 'ROAS'];
          changed = true;
        }
      }
      return changed ? next : prev;
    });
  }, [goalDefsNeedingDefaults]);

  const handleHeaderMenuComponentChange = (value: any) => setSelectedMemberWebsite(value);

  const lifecycleRows: LifecycleRow[] = [];

  // Contribution and Sources render through their own components; only their subtitles come from the backend.
  const dynamicContribution = insightsChartData.contribution;
  const contributionData = (dynamicContribution && dynamicContribution.contribution) ? dynamicContribution.contribution : null;
  const dynamicSources = insightsChartData.sources;
  const sourcesData = (dynamicSources && dynamicSources.sources) ? dynamicSources.sources : null;
  const insightsSubtitles: Record<string, string> = {
    contribution: dynamicContribution?.chart_subtitle || '',
    sources: dynamicSources?.chart_subtitle || '',
  };

  return (
    <>
      <VisiLoader VisiLoaderVisible={reportLoading || saving || inflight > 0 || drillPending} loaderType="report" />
      {showAllAdHelpPos && createPortal(
        <SelectTooltip
          isVisible={true}
          setIsVisible={(v: boolean) => { if (!v) { setShowAllAdHelpPos(null); } }}
          id={335}
          tooltipPosition={showAllAdHelpPos}
        />,
        document.body
      )}
      <HeaderMenu
        HeaderMenuValue={selectedMemberWebsite}
        HeaderMenuOnChange={handleHeaderMenuComponentChange}
      />

      <Container size="xl" py="md" fluid={tableExpanded}>
        <Card
          shadow="sm"
          padding="md"
          radius="md"
          withBorder
          mb="md"
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
          })}
        >
          <Flex align="center" gap="xs" mb="sm" wrap="wrap">
            {reportNameEditing ? (
              <>
                <TextInput
                  value={reportNameDraft}
                  onChange={(e) => setReportNameDraft(e.currentTarget.value)}
                  placeholder="Report name"
                  size="sm"
                  autoFocus
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      const next = reportNameDraft.trim();
                      if (next !== '') { setReportName(next); }
                      setReportNameEditing(false);
                    } else if (e.key === 'Escape') {
                      setReportNameEditing(false);
                    }
                  }}
                  sx={{ flex: '1 1 320px', maxWidth: 480 }}
                />
                <ActionIcon
                  color="teal"
                  variant="filled"
                  onClick={() => {
                    const next = reportNameDraft.trim();
                    if (next !== '') { setReportName(next); }
                    setReportNameEditing(false);
                  }}
                  aria-label="Confirm report name"
                >
                  <IconCheck size="1rem" />
                </ActionIcon>
                <ActionIcon
                  variant="default"
                  onClick={() => setReportNameEditing(false)}
                  aria-label="Cancel rename"
                >
                  <IconX size="1rem" />
                </ActionIcon>
              </>
            ) : (
              <>
                <Title order={3} sx={{ lineHeight: 1.2 }}>
                  {reportName || 'Untitled Report'}
                </Title>
                <ActionIcon
                  variant="subtle"
                  onClick={() => {
                    setReportNameDraft(reportName);
                    setReportNameEditing(true);
                  }}
                  aria-label="Rename report"
                  disabled={(!reportId && !isNewReport) || reportLoading}
                >
                  <IconPencil size="1rem" />
                </ActionIcon>
              </>
            )}
          </Flex>

          <Flex justify="space-between" align="center" wrap="wrap" gap="md">
            <Flex align="center" gap="xl" wrap="wrap">
              <Flex align="center" gap="xs">
                <AttributionHelp
                  multiCampaign={selectedVisioptCampaigns.length > 1}
                  attribution={attribution}
                />
                <Text color="dimmed" size="sm">
                  Attribution
                </Text>
                <Select
                  value={attribution}
                  onChange={setAttribution}
                  data={[
                    { value: 'first_click', label: 'First Click' },
                    { value: 'last_click', label: 'Last Click' },
                    { value: 'full_impact', label: 'Full Impact' },
                    { value: 'same_day', label: 'Same Day' },
                  ]}
                  size="sm"
                  w={200}
                  withinPortal
                  dropdownPosition="bottom"
                />
                {/* Says out loud that a Lookback Window is narrowing every conversion count on
                    this report. Hidden under True Customer Journey, where nothing is narrowed.
                    Clicking opens the drawer the window is set in. */}
                {attributionMode === 'lookback' && (
                  <Tooltip
                    multiline
                    width={280}
                    withArrow
                    withinPortal
                    position="bottom"
                    label={`Conversions only count when the sale happened within ${lookbackDays} days of the landing page visit.`}
                  >
                    <Badge
                      color="teal"
                      variant="outline"
                      size="sm"
                      sx={{ textTransform: 'none', cursor: 'pointer' }}
                      onClick={() => openSettings('Total Settings', 'Applies to Total · Goal Breakdown')}
                    >
                      {lookbackDays}-day window
                    </Badge>
                  </Tooltip>
                )}
              </Flex>

              <Flex align="center" gap="xs">
                <Text color="dimmed" size="sm">
                  Date Range
                </Text>
                {(isNewReport || loadedReportId === reportId) ? (
                  <SelectTimePicker
                    key={reportId || 'new'}
                    SelectTimePickerValue={selectedTimePicker}
                    SelectTimePickerOnChange={handleTimePickerChange}
                    SelectTimePickerFromDateOnChange={handleTimePickerFromDateChange}
                    SelectTimePickerToDateOnChange={handleTimePickerToDateChange}
                    SelectTestStartDate={(() => {
                      const d = new Date();
                      return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(
                        2,
                        '0'
                      )}-${String(d.getDate()).padStart(2, '0')}`;
                    })()}
                  />
                ) : (
                  <Select
                    size="sm"
                    w={170}
                    data={[]}
                    placeholder="Loading…"
                    disabled
                  />
                )}
              </Flex>
            </Flex>

            <Button
              leftIcon={<IconDeviceFloppy size="1.1rem" />}
              color="teal"
              onClick={() => handleSaveView()}
              loading={saving}
              disabled={(!reportId && !isNewReport) || reportLoading}
            >
              {isNewReport ? 'Save' : 'Update View'}
            </Button>
          </Flex>
        </Card>

        <Card
          shadow="sm"
          padding="md"
          radius="md"
          withBorder
          mb="md"
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
          })}
        >
          <Flex justify="space-between" align="center" wrap="wrap" gap="sm">
            <Flex align="center" gap="sm" wrap="wrap">
              <Text weight={700} size="sm" transform="uppercase" sx={{ letterSpacing: 0.5 }}>
                Filters
              </Text>

              {activeFilters.some((f) => !f.fixed) && (
                <Badge variant="outline" color="teal" size="sm" sx={{ textTransform: 'none' }}>
                  {activeFilters.filter((f) => !f.fixed).length} active
                </Badge>
              )}

              <ActionIcon
                variant="subtle"
                size="sm"
                onClick={() => setFiltersOpened((o) => !o)}
                title={filtersOpened ? 'Collapse' : 'Expand'}
              >
                {filtersOpened ? <IconChevronUp size="1rem" /> : <IconChevronDown size="1rem" />}
              </ActionIcon>

              {activeFilters.map((filter) => (
                <Badge
                  key={filter.key}
                  size="lg"
                  radius="sm"
                  variant="outline"
                  sx={(theme) => ({
                    textTransform: 'none',
                    borderColor:
                      theme.colorScheme === 'dark'
                        ? theme.colors.dark[3]
                        : theme.colors.gray[4],
                    paddingRight: 6,
                  })}
                  rightSection={filter.fixed ? undefined : (
                    <ActionIcon
                      size="xs"
                      variant="transparent"
                      onClick={() => removeFilter(filter.key)}
                    >
                      <IconX size="0.75rem" />
                    </ActionIcon>
                  )}
                >
                  <Text component="span" size="sm">
                    <Text component="span" color="dimmed" size="sm">
                      {filter.label}:{' '}
                    </Text>
                    <Text component="span" weight={600} size="sm">
                      {filter.value}
                    </Text>
                  </Text>
                </Badge>
              ))}
            </Flex>
          </Flex>

          <Collapse in={filtersOpened}>
            <Stack spacing="md" mt="md">
              <Box>
                <Text
                  size="xs"
                  weight={700}
                  transform="uppercase"
                  color="dimmed"
                  mb="xs"
                  sx={{ letterSpacing: 0.5 }}
                >
                  Site
                </Text>
                <Box sx={{ maxWidth: 420 }}>
                  <MultiSelect
                    label="Domains"
                    placeholder="Select domains"
                    value={selectedDomains}
                    onChange={setSelectedDomains}
                    searchable
                    clearable
                    nothingFound="No domains"
                    data={domainOptions}
                    withinPortal
                    dropdownPosition="bottom"
                    maxDropdownHeight={280}
                  />
                </Box>
              </Box>

              <Divider />

              <Box>
                <Text
                  size="xs"
                  weight={700}
                  transform="uppercase"
                  color="dimmed"
                  mb="xs"
                  sx={{ letterSpacing: 0.5 }}
                >
                  Visiopt
                </Text>
                <Box sx={{ maxWidth: 320 }}>
                  <MultiSelect
                    label="Visiopt Campaigns"
                    placeholder="Select Visiopt campaigns"
                    value={selectedVisioptCampaigns}
                    onChange={setSelectedVisioptCampaigns}
                    searchable
                    clearable
                    nothingFound="No campaigns"
                    data={visioptCampaignOptions}
                    withinPortal
                    dropdownPosition="bottom"
                    maxDropdownHeight={280}
                  />
                </Box>
                {primaryGoalChoices.length > 0 && (
                  <Box mt="md" sx={{ maxWidth: 320 }}>
                    <Select
                      label="Primary Goal"
                      placeholder="Choose a primary goal"
                      data={primaryGoalChoices.map((o) => ({
                        value: o.key,
                        label: o.key === defaultPrimaryGoalKey ? `${o.label} (campaign default)` : o.label,
                      }))}
                      value={selectedPrimaryGoalKey || null}
                      onChange={(v) => setSelectedPrimaryGoalKey(v || '')}
                      clearable
                      withinPortal
                      dropdownPosition="bottom"
                    />
                    {!selectedPrimaryGoalKey && (
                      <Alert
                        color="yellow"
                        variant="light"
                        mt="xs"
                        icon={<IconAlertTriangle size="1rem" />}
                        sx={{ padding: '8px 12px' }}
                      >
                        <Text size="xs">
                          Selected campaigns have different primary goals. Choose which goal to use for reporting.
                        </Text>
                      </Alert>
                    )}
                  </Box>
                )}
              </Box>

              <Divider />

              <Box>
                <Text
                  size="xs"
                  weight={700}
                  transform="uppercase"
                  color="dimmed"
                  mb="xs"
                  sx={{ letterSpacing: 0.5 }}
                >
                  Traffic
                </Text>
                <Stack spacing="md">
                  {/* Ad hierarchy in order: network, account, campaign, ad set, ad. */}
                  <SimpleGrid
                    cols={3}
                    spacing="md"
                    breakpoints={[
                      { maxWidth: 'md', cols: 2 },
                      { maxWidth: 'xs', cols: 1 },
                    ]}
                  >
                    <MultiSelect
                      label="Ad Network"
                      placeholder="Select ad networks"
                      value={selectedTrafficSources}
                      onChange={setSelectedTrafficSources}
                      searchable
                      clearable
                      nothingFound="No ad networks"
                      data={trafficSourceOptions}
                      withinPortal
                      dropdownPosition="bottom"
                      maxDropdownHeight={280}
                    />
                    <MultiSelect
                      label="Ad Accounts"
                      placeholder="Select ad accounts"
                      value={selectedAdAccounts}
                      onChange={setSelectedAdAccounts}
                      searchable
                      clearable
                      nothingFound="No ad accounts"
                      data={adAccountOptions}
                      withinPortal
                      dropdownPosition="bottom"
                      maxDropdownHeight={280}
                    />
                    <MultiSelect
                      label="Campaigns"
                      placeholder="Select campaigns"
                      value={selectedAdCampaigns}
                      onChange={setSelectedAdCampaigns}
                      searchable
                      clearable
                      nothingFound="No campaigns"
                      data={adCampaignOptions}
                      withinPortal
                      dropdownPosition="bottom"
                      maxDropdownHeight={280}
                    />
                    <MultiSelect
                      label="Ad Sets"
                      placeholder="Select ad sets"
                      value={selectedAdSets}
                      onChange={setSelectedAdSets}
                      searchable
                      clearable
                      nothingFound="No ad sets"
                      data={adSetOptions}
                      withinPortal
                      dropdownPosition="bottom"
                      maxDropdownHeight={280}
                    />
                    <MultiSelect
                      label="Ads"
                      placeholder="Select ads"
                      value={selectedAds}
                      onChange={setSelectedAds}
                      searchable
                      clearable
                      nothingFound="No ads"
                      data={adOptions}
                      withinPortal
                      dropdownPosition="bottom"
                      maxDropdownHeight={280}
                    />
                  </SimpleGrid>
                  {/* Report-wide display settings, applied with Update View. */}
                  <Group spacing="xl">
                    {/* Spend rule: strip, tables, Goal Breakdown and charts. */}
                    <Switch
                      checked={showAllAdCost}
                      onChange={(e) => setShowAllAdCost(e.currentTarget.checked)}
                      color="teal"
                      label={
                        <Flex align="center" gap={6}>
                          <Text size="sm" weight={500}>Show All Ad Costs</Text>
                          <IconHelp
                            ref={showAllAdHelpRef}
                            size="1.1rem"
                            color="red"
                            style={{ cursor: 'pointer' }}
                            onClick={(e) => {
                              e.preventDefault();
                              const rect = showAllAdHelpRef.current?.getBoundingClientRect();
                              if (!rect) { return; }
                              const tooltipHeight = 150;
                              const top = window.innerHeight - rect.bottom >= tooltipHeight
                                ? rect.bottom + window.scrollY + 8
                                : rect.top + window.scrollY - tooltipHeight - 8;
                              setShowAllAdHelpPos({ top, left: Math.min(rect.left + window.scrollX, window.innerWidth - 410) });
                            }}
                          />
                        </Flex>
                      }
                    />
                    <Switch
                      checked={showOrganicDirect}
                      onChange={(e) => setShowOrganicDirect(e.currentTarget.checked)}
                      color="teal"
                      label={
                        <Flex align="center" gap={6}>
                          <Text size="sm" weight={500}>Show Organic + Direct Traffic</Text>
                          <Tooltip
                            multiline
                            width={320}
                            withArrow
                            withinPortal
                            position="top-start"
                            label="Adds Organic and Direct rows to the Campaign tab. The rest of the report doesn't change. To filter the whole report by source, use Traffic Source."
                          >
                            <Box component="span" sx={{ display: 'inline-flex', cursor: 'pointer' }}>
                              <IconHelp size="1.1rem" color="red" />
                            </Box>
                          </Tooltip>
                        </Flex>
                      }
                    />
                  </Group>
                </Stack>
              </Box>

              <Divider />

              <AdvancedFilters
                enabled={advancedFiltersEnabled}
                campaignId={advancedFilterCampaignId}
                campaignIds={selectedVisioptCampaigns}
                timeParams={apiTimeParams}
                value={advancedFilters}
                onChange={setAdvancedFilters}
                refreshKey={dropdownRefreshKey}
                trafficSourceOptions={sourceItemOptions}
                trafficSourceValue={selectedSourceItems}
                onTrafficSourceChange={setSelectedSourceItems}
              />

              <Flex justify="flex-end">
                <Button variant="subtle" color="gray" onClick={clearAllFilters}>
                  Clear All
                </Button>
              </Flex>
            </Stack>
          </Collapse>
        </Card>

        <Card
          shadow="sm"
          padding="md"
          radius="md"
          withBorder
          sx={(theme) => ({
            backgroundColor:
              theme.colorScheme === 'dark' ? theme.colors.dark[7] : theme.white,
          })}
        >
          <Tabs
            value={activeTab}
            onTabChange={(v) => {
              if (v === activeTab) { return; }
              /* Hold the loader through the next paint so a tab that still has to fetch shows the
                 spinner instead of empty content. A tab that already has its data skips the hold,
                 which would otherwise flash a spinner over data that is ready to render. */
              const tabNeedsLoad = v === 'total'    ? !primaryGoalSummary
                                 : v === 'funnel'   ? aggregatedFunnelRows.length === 0
                                 : v === 'campaign' ? !networkCampaignData
                                 : v === 'adset'    ? !networkAdSetData
                                 : !networkAdData;
              if (tabNeedsLoad) {
                setInflight((c) => c + 1);
                requestAnimationFrame(() => setInflight((c) => Math.max(0, c - 1)));
              }
              // Clicking a tab clears any active drill chain below it.
              if (v === 'campaign') {
                setParentCampaignIds([]);
                setParentAdsetIds([]);
              } else if (v === 'adset') {
                setParentAdsetIds([]);
              }
              setActiveTab(v);
            }}
            color="teal"
          >
            <Flex align="stretch">
              <Tabs.List sx={{ flex: 1, minWidth: 0 }}>
                <Tabs.Tab value="total" style={{ fontSize: 14, fontWeight: 500 }}>
                  Total
                </Tabs.Tab>
                <Tabs.Tab value="funnel" style={{ fontSize: 14, fontWeight: 500 }}>
                  Funnel (Visiopt Campaign)
                </Tabs.Tab>
                <Tabs.Tab value="campaign" style={{ fontSize: 14, fontWeight: 500 }}>
                  Campaign (Ad Network Campaign)
                </Tabs.Tab>
                <Tabs.Tab value="adset" style={{ fontSize: 14, fontWeight: 500 }}>
                  Ad Set
                </Tabs.Tab>
                <Tabs.Tab value="ad" style={{ fontSize: 14, fontWeight: 500 }}>
                  Ad
                </Tabs.Tab>
              </Tabs.List>
              <Flex
                align="center"
                pl="sm"
                sx={(theme) => ({
                  borderBottom: `2px solid ${
                    theme.colorScheme === 'dark' ? theme.colors.dark[4] : theme.colors.gray[3]
                  }`,
                })}
              >
                <Button
                  variant="default"
                  size="xs"
                  leftIcon={
                    tableExpanded
                      ? <IconArrowsRightLeft size="0.9rem" />
                      : <IconArrowsHorizontal size="0.9rem" />
                  }
                  title={tableExpanded ? 'Shrink the report to the standard width' : 'Expand the report to the full window width'}
                  onClick={toggleTableExpanded}
                >
                  {tableExpanded ? 'Shrink' : 'Expand'}
                </Button>
              </Flex>
            </Flex>

            <TotalTab
              goalRows={goalBreakdownRows}
              goalCustomerType={goalCustomerType}
              primaryGoalSummary={primaryGoalSummary}
              lifecycleData={lifecycleData}
              lifecycleLoading={inflight > 0}
              isDarkTheme={colorTheme === 'dark'}
              insightsSubtitles={insightsSubtitles}
              insightsChart={insightsChart}
              setInsightsChart={setInsightsChart}
              trendData={insightsChartData[insightsChart]?.trend ?? null}
              velocityData={insightsChartData.velocity?.velocity ?? null}
              insightsCompare={insightsCompare}
              onInsightsCompareChange={handleInsightsCompareChange}
              cpcTrendMetric={cpcTrendMetric}
              onCpcTrendMetricChange={handleCpcTrendMetricChange}
              onVelocityFromChange={handleVelocityFromChange}
              contributionData={contributionData}
              sourcesData={sourcesData}
              openSettings={openSettings}
              totalMetrics={totalMetrics}
              primaryGoalNames={uniquePrimaryGoalOptions.map((o) => o.label)}
              selectedPrimaryGoalName={selectedPrimaryGoalName}
              advancedFilterTables={advancedFilterTables}
              goalBreakdownOpened={totalGoalBreakdownOpen}
              goalBreakdownType={totalBreakdown}
              onGoalBreakdownTypeChange={(v) => { setTotalBreakdownDraft(v); setTotalBreakdown(v); }}
              setGoalBreakdownOpened={setTotalGoalBreakdownOpen}
              lifecycleOpened={totalLifecycleOpen}
              setLifecycleOpened={setTotalLifecycleOpen}
              insightsOpened={totalInsightsOpen}
              setInsightsOpened={setTotalInsightsOpen}
              onGoalJourneyOpen={openGoalJourney}
              onGoalConversionLogOpen={openTotalConversionLog}
              onSourceJourneyOpen={openSourceJourney}
              onTrackingJourneyOpen={openTrackingJourney}
              onCustomJourneyOpen={openCustomJourney}
              onLifecycleVelocityOpen={openLifecycleVelocity}
            />

            <FunnelTab
              multiCampaign={selectedVisioptCampaigns.length > 1}
              attribution={attribution}
              funnelRows={selectedVisioptCampaigns.length > 0 ? aggregatedFunnelRows : []}
              goalRows={selectedFunnelId ? funnelDrillGoalRows : []}
              goalCustomerType={goalCustomerType}
              drillPrimarySummary={funnelDrillPrimarySummary}
              drillLifecycle={funnelDrillLifecycle}
              isDarkTheme={colorTheme === 'dark'}
              drillLoading={inflight > 0}
              lifecycleRows={lifecycleRows}
              lifecycleGroupBy={lifecycleGroupBy}
              setLifecycleGroupBy={setLifecycleGroupBy}
              trafficBaseParams={funnelTrafficBaseParams}
              trafficBreakdownType={breakdownType}
              trafficMetrics={trafficMetrics}
              trafficFunnelMetricsByGoal={trafficFunnelMetricsByGoal}
              trafficGoalsEnabled={trafficGoalsEnabled}
              onFunnelTrafficGoalsChange={setFunnelTrafficGoalDefs}
              selectedFunnelId={selectedFunnelId}
              setSelectedFunnelId={openFunnelDrill}
              openSettings={openSettings}
              funnelAdditionalMetrics={funnelAdditionalMetrics}
              testVariationActive={testVariationActive}
              testVariationMessage={testVariationInfo.message}
              onVariationOpen={openVariationAds}
              onVariationJourneyOpen={openVariationJourney}
              onVariationConversionLogOpen={openVariationConversionLog}
              advancedFilterTables={funnelAdvancedFilterTables}
              goalBreakdownOpened={funnelGoalBreakdownOpen}
              goalBreakdownType={funnelBreakdown === 'cust_type' ? 'none' : funnelBreakdown}
              onGoalBreakdownTypeChange={(v) => { setFunnelBreakdownDraft(v); setFunnelBreakdown(v); }}
              setGoalBreakdownOpened={setFunnelGoalBreakdownOpen}
              lifecycleOpened={funnelLifecycleOpen}
              setLifecycleOpened={setFunnelLifecycleOpen}
              trafficOpened={funnelTrafficOpen}
              setTrafficOpened={setFunnelTrafficOpen}
              onGoalJourneyOpen={openGoalJourney}
              onCampaignNotesOpen={openCampaignNotes}
              onGoalConversionLogOpen={openFunnelConversionLog}
              onSourceJourneyOpen={openSourceJourney}
              onTrackingJourneyOpen={openTrackingJourney}
              onCustomJourneyOpen={openCustomJourney}
              onTrafficJourneyOpen={openTrafficJourney}
              onLifecycleVelocityOpen={openLifecycleVelocity}
              onKeywordExport={handleKeywordExport}
              hiddenCampaignKeys={hiddenCampaignKeys}
              onToggleHiddenCampaign={toggleHiddenCampaign}
              trafficVelocityContext={{
                report_id: reportId,
                // This table is scoped to the drilled funnel, not the whole report selection.
                visiopt_campaign_id: selectedFunnelId ? [selectedFunnelId] : [],
                filter_visiopt_campaign_ids: selectedVisioptCampaigns,
                attribution_model: attribution ?? '',
                attribute_window_type: attributionMode,
                lookback_days: parseInt(lookbackDays, 10),
                report_time: (apiTimeParams as any)?.report_time ?? '-1',
                ad_account_id: selectedAdAccounts,
                test_variation_enabled: testVariationActive ? 1 : 0,
              }}
            />

            <AdNetworkTab
              onKeywordExport={handleKeywordExport}
              breakdownType={breakdownType}
              level="campaign"
              hiddenCampaignKeys={hiddenCampaignKeys}
              onToggleHiddenCampaign={toggleHiddenCampaign}
              hiddenRows={(networkCampaignData?.hidden_rows ?? []) as NetworkRow[]}
              rows={(networkCampaignData?.rows ?? []) as NetworkRow[]}
              goals={campaignGoalDefs}
              eligible={!!networkCampaignData?.eligible}
              emptyMessage={!networkCampaignData ? 'Select a campaign filter to load data.' : undefined}
              loading={inflight > 0 && !networkCampaignData}
              openSettings={openSettings}
              selectedIds={selectedCampaignIds}
              setSelectedIds={setSelectedCampaignIds}
              trafficMetrics={trafficMetrics}
              trafficFunnelMetricsByGoal={trafficFunnelMetricsByGoal}
              trafficGoalsEnabled={trafficGoalsEnabled}
              search={campaignSearchInput}
              onSearchChange={setCampaignSearchInput}
              page={networkCampaignData?.pagination?.page ?? campaignPage}
              onPageChange={setCampaignPage}
              totalPages={networkCampaignData?.pagination?.total_pages ?? 1}
              totalRecords={networkCampaignData?.pagination?.total_records ?? 0}
              perPage={networkCampaignData?.pagination?.per_page ?? NETWORK_PER_PAGE}
              sort={campaignSort}
              onSortChange={onCampaignSortChange}
              velocityContext={{
                report_id: reportId,
                visiopt_campaign_id: selectedVisioptCampaigns,
                attribution_model: attribution ?? '',
                attribute_window_type: attributionMode,
                lookback_days: parseInt(lookbackDays, 10),
                report_time: (apiTimeParams as any)?.report_time ?? '-1',
                // Keyword View resolves the ad group's Google account from the report's own
                // account filter - the rows themselves are built from it, not from campaign_data.
                ad_account_id: selectedAdAccounts,
                attribution_label: networkCampaignData?.attribution_used,
              }}
              onDrillDown={(row) => {
                // Prefer checkbox selection over clicked row.
                const ids =
                  selectedCampaignIds.length > 0 ? selectedCampaignIds : [row.id];
                setParentCampaignIds(ids);
                setParentAdsetIds([]);
                setAdsetPage(1); setAdsetSearchInput(''); setAdsetSearch('');
                setAdPage(1); setAdSearchInput(''); setAdSearch('');
                setActiveTab('adset');
              }}
            />
            <AdNetworkTab
              onKeywordExport={handleKeywordExport}
              breakdownType={breakdownType}
              level="adset"
              hiddenCampaignKeys={hiddenCampaignKeys}
              onToggleHiddenCampaign={toggleHiddenCampaign}
              hiddenRows={(networkAdSetData?.hidden_rows ?? []) as NetworkRow[]}
              rows={(networkAdSetData?.rows ?? []) as NetworkRow[]}
              goals={adSetGoalDefs}
              eligible={!!networkAdSetData?.eligible}
              emptyMessage={!networkAdSetData ? 'Select a campaign filter to load data.' : undefined}
              loading={inflight > 0 && !networkAdSetData}
              openSettings={openSettings}
              selectedIds={selectedAdsetIds}
              setSelectedIds={setSelectedAdsetIds}
              trafficMetrics={trafficMetrics}
              trafficFunnelMetricsByGoal={trafficFunnelMetricsByGoal}
              trafficGoalsEnabled={trafficGoalsEnabled}
              search={adsetSearchInput}
              onSearchChange={setAdsetSearchInput}
              page={networkAdSetData?.pagination?.page ?? adsetPage}
              onPageChange={setAdsetPage}
              totalPages={networkAdSetData?.pagination?.total_pages ?? 1}
              totalRecords={networkAdSetData?.pagination?.total_records ?? 0}
              perPage={networkAdSetData?.pagination?.per_page ?? NETWORK_PER_PAGE}
              sort={adsetSort}
              onSortChange={onAdsetSortChange}
              velocityContext={{
                report_id: reportId,
                visiopt_campaign_id: selectedVisioptCampaigns,
                attribution_model: attribution ?? '',
                attribute_window_type: attributionMode,
                lookback_days: parseInt(lookbackDays, 10),
                report_time: (apiTimeParams as any)?.report_time ?? '-1',
                // Keyword View resolves the ad group's Google account from the report's own
                // account filter - the rows themselves are built from it, not from campaign_data.
                ad_account_id: selectedAdAccounts,
                attribution_label: networkAdSetData?.attribution_used,
              }}
              drillFilterLabel={
                parentCampaignIds.length === 0
                  ? undefined
                  : parentCampaignIds.length === 1
                  ? networkCampaignData?.rows.find((r) => r.id === parentCampaignIds[0])?.campaign
                    ?? '1 campaign'
                  : `${parentCampaignIds.length} campaigns`
              }
              onClearDrillFilter={
                parentCampaignIds.length > 0
                  ? () => {
                      setParentCampaignIds([]);
                      setSelectedCampaignIds([]);
                      setAdsetPage(1); setAdsetSearchInput(''); setAdsetSearch('');
                    }
                  : undefined
              }
              onDrillDown={(row) => {
                const ids =
                  selectedAdsetIds.length > 0 ? selectedAdsetIds : [row.id];
                setParentAdsetIds(ids);
                setAdPage(1); setAdSearchInput(''); setAdSearch('');
                setActiveTab('ad');
              }}
            />
            <AdNetworkTab
              onKeywordExport={handleKeywordExport}
              breakdownType={breakdownType}
              level="ad"
              hiddenCampaignKeys={hiddenCampaignKeys}
              onToggleHiddenCampaign={toggleHiddenCampaign}
              hiddenRows={(networkAdData?.hidden_rows ?? []) as NetworkRow[]}
              rows={(networkAdData?.rows ?? []) as NetworkRow[]}
              goals={adGoalDefs}
              eligible={!!networkAdData?.eligible}
              emptyMessage={!networkAdData ? 'Select a campaign filter to load data.' : undefined}
              loading={inflight > 0 && !networkAdData}
              openSettings={openSettings}
              selectedIds={selectedAdIds}
              setSelectedIds={setSelectedAdIds}
              trafficMetrics={trafficMetrics}
              trafficFunnelMetricsByGoal={trafficFunnelMetricsByGoal}
              trafficGoalsEnabled={trafficGoalsEnabled}
              search={adSearchInput}
              onSearchChange={setAdSearchInput}
              page={networkAdData?.pagination?.page ?? adPage}
              onPageChange={setAdPage}
              totalPages={networkAdData?.pagination?.total_pages ?? 1}
              totalRecords={networkAdData?.pagination?.total_records ?? 0}
              perPage={networkAdData?.pagination?.per_page ?? NETWORK_PER_PAGE}
              sort={adSort}
              onSortChange={onAdSortChange}
              /* The report decides whether the chosen attribution model can produce assists,
                 so the Ad tab shows numbers or the switch-attribution note without ever
                 re-deriving the rule from the model name. */
              assistsSupported={!!networkAdData?.assists_supported}
              assistsLoaded={!!networkAdData?.assists_loaded}
              assistsMessage={networkAdData?.assists_message}
              assistContext={networkAdAssistContext}
              velocityContext={{
                report_id: reportId,
                visiopt_campaign_id: selectedVisioptCampaigns,
                attribution_model: attribution ?? '',
                attribute_window_type: attributionMode,
                lookback_days: parseInt(lookbackDays, 10),
                report_time: (apiTimeParams as any)?.report_time ?? '-1',
                // Keyword View resolves the ad group's Google account from the report's own
                // account filter - the rows themselves are built from it, not from campaign_data.
                ad_account_id: selectedAdAccounts,
                attribution_label: networkAdData?.attribution_used,
              }}
              drillFilterLabel={
                parentAdsetIds.length > 0
                  ? parentAdsetIds.length === 1
                    ? networkAdSetData?.rows.find((r) => r.id === parentAdsetIds[0])?.adset
                      ?? '1 ad set'
                    : `${parentAdsetIds.length} ad sets`
                  : parentCampaignIds.length > 0
                  ? parentCampaignIds.length === 1
                    ? networkCampaignData?.rows.find((r) => r.id === parentCampaignIds[0])?.campaign
                      ?? '1 campaign'
                    : `${parentCampaignIds.length} campaigns`
                  : undefined
              }
              onClearDrillFilter={
                parentAdsetIds.length > 0
                  ? () => {
                      setParentAdsetIds([]);
                      setSelectedAdsetIds([]);
                      setAdPage(1); setAdSearchInput(''); setAdSearch('');
                    }
                  : parentCampaignIds.length > 0
                  ? () => {
                      setParentCampaignIds([]);
                      setSelectedCampaignIds([]);
                      setAdsetPage(1); setAdsetSearchInput(''); setAdsetSearch('');
                      setAdPage(1); setAdSearchInput(''); setAdSearch('');
                    }
                  : undefined
              }
            />
          </Tabs>
        </Card>
      </Container>

      <SettingsDrawer
        opened={settingsOpen}
        onClose={requestDrawerClose}
        onSave={handleDrawerSave}
        dirty={drawerDirty}
        saving={saving}
        context={settingsContext}
        scope={settingsScope}
        visibleMetrics={currentMetrics}
        setVisibleMetrics={setCurrentMetrics}
        metricsLabel={
          settingsContext === 'Funnel Settings' ? 'Additional Metrics' : 'Visible Metrics'
        }
        benchmarks={benchmarks}
        benchmarksOn={benchmarksOn}
        setBenchmarksOn={setBenchmarksOn}
        addBenchmark={addBenchmark}
        removeBenchmark={removeBenchmark}
        updateBenchmark={updateBenchmark}
        testVariationVisible={
          settingsContext === 'Funnel Settings'
          && !(selectedVisioptCampaigns.length === 1
               && !aggregatedFunnelRows.some((r) => r.has_test_variation === true))
        }
        testVariationActive={testVariationDraft}
        onToggleTestVariation={() => setTestVariationDraft((v) => !v)}
        trafficMetrics={trafficMetrics}
        setTrafficMetrics={setTrafficMetrics}
        trafficFunnelMetricsByGoal={trafficFunnelMetricsByGoal}
        setTrafficFunnelMetricsByGoal={setTrafficFunnelMetricsByGoal}
        trafficGoalsEnabled={trafficGoalsEnabled}
        setTrafficGoalsEnabled={setTrafficGoalsEnabled}
        trafficGoals={dynamicGoalNames}
        goalHasRevenue={goalHasRevenue}
        benchmarkGoalOptions={dynamicGoalOptions}
        attributionModeVisible={
          settingsContext === 'Total Settings' || settingsContext === 'Campaign Settings'
        }
        attributionMode={attributionMode}
        setAttributionMode={setAttributionMode}
        lookbackDays={lookbackDays}
        setLookbackDays={setLookbackDays}
        breakdownType={breakdownType}
        setBreakdownType={setBreakdownType}
        resetStatsVisible={
          settingsContext === 'Total Settings'
          && hasPermission('operational_access')
          && selectedVisioptCampaigns.length > 0
        }
        onResetStats={() => setResetStatsOpen(true)}
        onExport={settingsExportable ? handleExport : undefined}
        exporting={exporting}
      />

      <AggregateCampaignNotes
        opened={campaignNotesOpen}
        onClose={() => setCampaignNotesOpen(false)}
        campaignId={campaignNotesTarget.id === '' ? null : campaignNotesTarget.id}
        campaignName={campaignNotesTarget.name}
        onSaved={handleCampaignNoteSaved}
      />

      <AggregateConversionLog
        opened={conversionLogOpen}
        onClose={() => setConversionLogOpen(false)}
        params={conversionLogParams}
      />

      <AggregateVariationAds
        opened={variationAdsOpen}
        onClose={() => setVariationAdsOpen(false)}
        params={variationAdsParams}
      />

      <AggregateResetStats
        opened={resetStatsOpen}
        onClose={() => setResetStatsOpen(false)}
        reportId={reportId}
        campaignIds={selectedVisioptCampaigns}
        onResetDone={handleResetStatsDone}
      />

      <AggregateCustomerJourney
        opened={journeyOpen}
        onClose={() => setJourneyOpen(false)}
        params={journeyParams}
        entityLabel={journeyEntityLabel}
      />

      <ReportVelocityDrawer
        report_id={reportId}
        showVelocityDrawer={lifecycleVelocityOpen}
        setShowVelocityDrawer={setLifecycleVelocityOpen}
        campaignGoalId=""
        encodeCampaignName=""
        selectedCampaignName=""
        adsetName=""
        trafficSource=""
        campaign_id=""
        trafficLevel=""
        goalType=""
        combinedGoalType={false}
        selectedAttributionModal={attribution ?? ''}
        displayCampaignName={lifecycleVelocityLabel}
        velocityMode="aggregate"
        aggregateVelocityParams={lifecycleVelocityParams}
      />

      <Modal
        opened={closeConfirmOpen}
        onClose={() => { if (!saving) { setCloseConfirmOpen(false); } }}
        title="Unsaved changes"
        centered
        closeOnClickOutside={!saving}
        closeOnEscape={!saving}
        withCloseButton={!saving}
        size="sm"
      >
        <Stack spacing="md">
          <Text size="sm">You made changes.</Text>
          <Text size="sm">Save changes or discard?</Text>
          <Group position="right" spacing="sm">
            <Button variant="default" onClick={confirmCloseDiscard} disabled={saving}>Discard</Button>
            <Button color="teal" onClick={confirmCloseSave} loading={saving}>Save</Button>
          </Group>
        </Stack>
      </Modal>

      <Modal
        opened={nameModalOpen}
        onClose={() => {
          if (saving) { return; }
          setNameModalOpen(false);
        }}
        title="Name this report"
        centered
        closeOnClickOutside={!saving}
        closeOnEscape={!saving}
        withCloseButton={!saving}
      >
        <Stack spacing="sm">
          <Text size="sm" color="dimmed">
            Give this report a name so you can find it later in the reports list.
          </Text>
          <TextInput
            label="Report name"
            placeholder="e.g. Q1 Paid Acquisition"
            value={nameModalDraft}
            onChange={(e) => {
              setNameModalDraft(e.currentTarget.value);
              if (nameModalError) { setNameModalError(''); }
            }}
            error={nameModalError || undefined}
            autoFocus
            data-autofocus
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                const next = nameModalDraft.trim();
                if (next === '') {
                  setNameModalError('Please enter a report name.');
                  return;
                }
                setReportName(next);
                setNameModalOpen(false);
                performSaveView(next);
              }
            }}
          />
          <Group position="right" spacing="xs">
            <Button
              variant="default"
              onClick={() => setNameModalOpen(false)}
              disabled={saving}
            >
              Cancel
            </Button>
            <Button
              color="teal"
              loading={saving}
              onClick={() => {
                const next = nameModalDraft.trim();
                if (next === '') {
                  setNameModalError('Please enter a report name.');
                  return;
                }
                setReportName(next);
                setNameModalOpen(false);
                performSaveView(next);
              }}
            >
              Save Report
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  );
}

export default Report;
